/**
 * OrgProjectsPage - 프로젝트 관리 페이지
 * V140.1: Drawer + 탭 구조로 UI 개선
 *
 * 조직관리 스코프 내의 프로젝트 목록을 표시합니다.
 * - 프로젝트 CRUD
 * - 수정(Edit) vs 갱신(Renew) 구분
 * - Drawer 내 탭: [상세] / [이력]
 */

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  Plus,
  Search,
  RefreshCw,
  RotateCcw,
  Trash2,
  ChevronRight,
  Calendar,
  Users,
  Building2,
  History,
  Edit3,
  RotateCw,
  X,
  Check,
  AlertCircle,
  Save,
  FileText,
  Clock,
} from 'lucide-react';
import { useOrgManagementScope } from '../../shared/hooks/useOrgManagementScope';
import { useOrgUnitsPublic } from '../../shared/hooks/useOrgUnitsPublic';
import { useOrgUnitSubtreeFilter } from '../../shared/hooks/useOrgUnitSubtreeFilter';
import { useProjects } from '../../shared/hooks/useProjects';
import {
  ProjectWithRevision,
  ProjectStatus,
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_COLORS,
  CreateProjectRequest,
  ProjectCustomer,
  ProjectContract,
  ProjectStaffing,
  ProjectRevision,
  EMPTY_CUSTOMER,
  EMPTY_CONTRACT,
  EMPTY_STAFFING,
  StaffGrade,
  STAFF_GRADES,
  ProjectLead,
  StaffMember,
  ContractedResource,
  createDefaultContractedResources,
  calculateTotalContractPrice,
  calculateGradeSubtotal,
  getTotalContractedHeadcount,
  aggregateStaffingByGrade,
  getTotalStaffingHeadcount,
  // V75: FTE 기반 타입/함수
  GradeBasedFTE,
  StaffAssignment,
  GRADE_TO_FTE_KEY,
  FTE_KEY_TO_GRADE,
  createEmptyGradeBasedFTE,
  getActualStaffingByGrade,
  getTotalActualStaffing,
  getTotalFTE,
  // V82: 편집 모드 타입
  ProjectEditMode,
  PROJECT_EDIT_MODE_LABELS,
  PROJECT_EDIT_MODE_DESCRIPTIONS,
  // V83: 예약 타입
  ProjectRenewalReservation,
  ProjectRenewalReservationStatus,
  // V99.3: scheduleRenew 요청 타입
  ScheduleProjectRenewalRequest,
  // V116.6: projectId로 로딩시 필요한 타입
  GetProjectHistoryRequest,
  GetProjectHistoryResponse,
} from '../../shared/types/project';
import { useEmployees } from '../../shared/hooks/useEmployees';
import { Employee } from '../../shared/types/employee';
import { useProjectTags } from '../../shared/hooks/useProjectTags';
import { ProjectTagMaster, PROJECT_TAG_TYPE_LABELS } from '../../shared/types/project';
// V91.3: 태그 마스터 탭
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { ADMIN_LIKE_ROLES } from '../../types/role';
import { ProjectTagMasterPanel } from './components/ProjectTagMasterPanel';
// V97: 고객사 마스터 탭
import { useCustomers, useCustomerMutations } from '../../shared/hooks/useCustomers';
import { Customer } from '../../shared/types/customer';
import { CustomerMasterPanel } from './components/CustomerMasterPanel';
// V54.11: MM마스터 On 조직 필터용
import { useMmOrgSettings } from '../../shared/hooks/useMmOrgSettings';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
// V69 (2026-05-20): 사용자 companyCode (국가 필터 default)
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';

/**
 * V91.3: 페이지 탭 타입
 * - projects: 프로젝트 목록
 * - tags: 태그 마스터 관리
 */
type PageTab = 'projects' | 'tags' | 'customers';

/**
 * V91.3: 태그 마스터 접근 허용 역할 Whitelist
 * - HR 제외: role_hr 없음
 */
const TAG_MASTER_ALLOWED_ROLES = [
  ...ADMIN_LIKE_ROLES,  // system_admin, role_admin_kr, role_admin_cn
  'role_manager',       // 조직장
  'role_exec',          // 임원
] as const;

/**
 * V97: 고객사 마스터 접근 허용 역할 (태그와 동일)
 */
const CUSTOMER_MASTER_ALLOWED_ROLES = TAG_MASTER_ALLOWED_ROLES;

/**
 * 스코프 모드 → 한글 라벨
 */
const SCOPE_MODE_LABELS = {
  SELF: '본인 조직만',
  SUBTREE: '하위 조직 포함',
  ALL: '전체 조직',
};

/**
 * V87: MM(Man-Month) 값 포맷 함수
 * - 0.5 단위 기반으로 반올림
 * - 정수는 소수점 없이, 소수는 1자리까지 출력
 * - undefined/NaN/Infinity → 0
 */
function formatMM(value?: number): string {
  const n = typeof value === 'number' && isFinite(value) ? value : 0;
  const rounded = Math.round(n * 2) / 2; // 0.5 단위 반올림
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

// ============================================
// V89.1: 등급 요약 표시 + 초과 배지 (레거시 actual.any 흡수)
// ============================================

/** V88: 실투입 등급 선택에서 '등급무관' 제외 */
const STAFF_GRADES_NO_ANY: StaffGrade[] = ['특급', '고급', '중급', '초급'];

/** V88: 등급 약어 매핑 */
const GRADE_ABBR: Record<StaffGrade, string> = {
  '특급': '특',
  '고급': '고',
  '중급': '중',
  '초급': '초',
  '등급무관': '무',
};

/** V99.7: 등급별 색상 매핑 */
const GRADE_COLOR_MAP: Record<string, string> = {
  '특': '#ef4444',
  '고': '#f97316',
  '중': '#eab308',
  '초': '#3b82f6',
} as const;

/** V88: 등급 순서 (특→고→중→초→무) */
const GRADE_ORDER: StaffGrade[] = ['특급', '고급', '중급', '초급', '등급무관'];

/** 실투입용 등급 순서 (무 제외) */
const GRADE_ORDER_NO_ANY: StaffGrade[] = ['특급', '고급', '중급', '초급'];

/** FTE 키 매핑 (공용) */
const FTE_KEYS: Record<StaffGrade, keyof GradeBasedFTE> = {
  '특급': 'special',
  '고급': 'senior',
  '중급': 'mid',
  '초급': 'junior',
  '등급무관': 'any',
};

/**
 * V89.1: 레거시 actual.any를 초급으로 흡수
 * - 실투입에는 '등급무관'이 없어야 하므로 any 값을 junior에 합산
 * - 반환값의 any는 항상 0
 */
function normalizeActualForOveruse(
  actual: GradeBasedFTE | null | undefined
): GradeBasedFTE {
  const a = actual || createEmptyGradeBasedFTE();
  return {
    special: a.special || 0,
    senior: a.senior || 0,
    mid: a.mid || 0,
    junior: (a.junior || 0) + (a.any || 0), // any를 junior로 흡수
    any: 0, // 실투입에는 any 없음
  };
}

/**
 * V89.1: 등급 토큰 인터페이스
 */
interface GradeToken {
  grade: StaffGrade;
  abbr: string;
  value: number;
}

/**
 * V89.1: 계약용 등급 토큰 생성 (무 포함)
 */
function getGradeTokensForContract(
  fte: GradeBasedFTE | null | undefined
): GradeToken[] {
  const data = fte || createEmptyGradeBasedFTE();
  const tokens: GradeToken[] = [];

  for (const grade of GRADE_ORDER) {
    const key = FTE_KEYS[grade];
    const val = data[key] || 0;
    if (val > 0) {
      tokens.push({
        grade,
        abbr: GRADE_ABBR[grade],
        value: val,
      });
    }
  }

  return tokens;
}

/**
 * V89.1: 실투입용 등급 토큰 생성 (무 제외, 레거시 any는 초급으로 흡수)
 */
function getGradeTokensForActual(
  fte: GradeBasedFTE | null | undefined
): GradeToken[] {
  const normalized = normalizeActualForOveruse(fte);
  const tokens: GradeToken[] = [];

  // 무 제외한 등급만 순회
  for (const grade of GRADE_ORDER_NO_ANY) {
    const key = FTE_KEYS[grade];
    const val = normalized[key] || 0;
    if (val > 0) {
      tokens.push({
        grade,
        abbr: GRADE_ABBR[grade],
        value: val,
      });
    }
  }

  return tokens;
}

/**
 * V89.1: 초과 토큰 인터페이스
 */
interface OveruseToken {
  grade: StaffGrade;
  abbr: string;
  value: number; // 초과량
}

/**
 * V89.1: 실투입 초과분 계산
 *
 * 정책:
 * - 레거시 actual.any는 초급으로 흡수 후 비교
 * - 계약의 '등급무관(any)'은 초급 초과분만 커버 가능
 * - 특급/고급/중급은 contract.any로 커버 불가
 * - 무 등급 자체 초과 비교는 하지 않음 (실투입에 무 없음)
 * - 초과된 수량만 반환
 */
function getOveruseTokens(
  actual: GradeBasedFTE | null | undefined,
  contract: GradeBasedFTE | null | undefined
): OveruseToken[] {
  // V89.1: 레거시 actual.any를 초급으로 흡수
  const a = normalizeActualForOveruse(actual);
  const c = contract || createEmptyGradeBasedFTE();

  const tokens: OveruseToken[] = [];

  // any 예산: 초급만 커버 가능
  const anyBudget = c.any || 0;

  // 무 제외한 등급만 비교 (특→고→중→초)
  for (const grade of GRADE_ORDER_NO_ANY) {
    const key = FTE_KEYS[grade];
    const actualVal = a[key] || 0;
    const contractVal = c[key] || 0;
    const diff = actualVal - contractVal;

    if (diff <= 0) continue; // 초과 없음

    let overuse = diff;

    if (grade === '초급') {
      // 초급만 anyBudget으로 커버 가능
      const covered = Math.min(diff, anyBudget);
      overuse = diff - covered;
    }
    // 특급, 고급, 중급: any로 커버 불가 (직접 초과)

    if (overuse > 0) {
      tokens.push({
        grade,
        abbr: GRADE_ABBR[grade],
        value: overuse,
      });
    }
  }

  return tokens;
}

/**
 * V129.2: 투입인원(actualStaffing) vs 실투입인원(staffAssignments) 등급별 매칭 검증
 *
 * 저장 시점에 actualStaffing(투입인원 등급별 MM)과 staffAssignments(직원별 등급+MM 합산)가
 * 등급별로 100% 일치하는지 검증합니다.
 *
 * @param actualStaffing - 투입인원 등급별 MM (GradeBasedFTE)
 * @param staffAssignments - 실투입 인원 배정 목록 (StaffAssignment[])
 * @returns 검증 결과 { valid, errors }
 *   - valid: 검증 통과 여부
 *   - errors: 불일치 에러 메시지 배열
 */
function validateStaffGradeMatchActualStaffing(
  actualStaffing: GradeBasedFTE | undefined,
  staffAssignments: StaffAssignment[]
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // 투입인원 정보가 없으면 검증 스킵
  if (!actualStaffing) {
    return { valid: true, errors: [] };
  }

  // staffAssignments가 없거나 비어있으면 검증 스킵
  if (!staffAssignments || staffAssignments.length === 0) {
    return { valid: true, errors: [] };
  }

  // staffAssignments에서 등급별 MM 집계
  const assignmentsByGrade: Record<string, number> = {};
  for (const assignment of staffAssignments) {
    const grade = assignment.grade || '등급무관';
    assignmentsByGrade[grade] = (assignmentsByGrade[grade] || 0) + assignment.fte;
  }

  // 등급별 비교 (투입인원 기준)
  const gradeMapping: Array<{ grade: StaffGrade; key: keyof GradeBasedFTE }> = [
    { grade: '특급', key: 'special' },
    { grade: '고급', key: 'senior' },
    { grade: '중급', key: 'mid' },
    { grade: '초급', key: 'junior' },
    { grade: '등급무관', key: 'any' },
  ];

  for (const { grade, key } of gradeMapping) {
    const actualMM = actualStaffing[key] || 0;
    const assignmentMM = assignmentsByGrade[grade] || 0;

    // 정수 비교 (*2하여 0.5 단위를 1 단위로 변환)
    const actualInt = Math.round(actualMM * 2);
    const assignmentInt = Math.round(assignmentMM * 2);

    if (actualMM > 0 && assignmentInt !== actualInt) {
      // 투입인원에 해당 등급이 있는데 실투입이 다른 경우
      if (assignmentMM === 0) {
        errors.push(`${grade}: 투입 ${actualMM}MM에 대해 실투입 인원이 없습니다.`);
      } else {
        errors.push(`${grade}: 투입 ${actualMM}MM ≠ 실투입 ${assignmentMM}MM`);
      }
    } else if (actualMM === 0 && assignmentMM > 0) {
      // 투입인원에 없는 등급으로 실투입한 경우
      errors.push(`${grade}: 투입인원에 없는 등급으로 ${assignmentMM}MM이 배정되었습니다.`);
    }
  }

  return { valid: errors.length === 0, errors };
}


/**
 * V89: 등급 토큰을 JSX로 렌더링
 * V99.7: 등급별 색상 적용 (특=빨강, 고=주황, 중=노랑, 초=파랑)
 */
function GradeBreakdownTokens({
  tokens,
  style,
}: {
  tokens: GradeToken[];
  style?: React.CSSProperties;
}) {
  if (tokens.length === 0) return null;

  return (
    <span style={{ fontSize: '12px', marginLeft: '4px', ...style }}>
      (
      {tokens.map((t, i) => (
        <span key={t.grade} style={{ color: GRADE_COLOR_MAP[t.abbr] || '#374151', fontWeight: 600 }}>
          {t.abbr}{formatMM(t.value)}
          {i < tokens.length - 1 && ' '}
        </span>
      ))}
      )
    </span>
  );
}

/**
 * V89: 초과 배지 컴포넌트
 * - 초과된 항목이 있을 때만 표시
 * - 빨간 배지로 명확히 구분
 */
function OveruseBadge({ tokens }: { tokens: OveruseToken[] }) {
  if (tokens.length === 0) return null;

  return (
    <span
      style={{
        marginLeft: 6,
        padding: '2px 6px',
        background: '#fee2e2',
        color: '#dc2626',
        border: '1px solid #fecaca',
        borderRadius: 6,
        fontSize: 11,
        whiteSpace: 'nowrap',
      }}
    >
      초과: {tokens.map(t => `${t.abbr}+${formatMM(t.value)}`).join(' ')}
    </span>
  );
}

/**
 * V91.1: 프로젝트 태그 표시 컴포넌트 (리스트용) - 캐시 최적화
 * - 페이지 레벨에서 로드한 tagMap을 prop으로 받아 사용
 * - 업무형태 태그는 보라색 배경 (윗줄)
 * - 프로젝트 분야 태그는 초록색 배경 (아랫줄)
 * V99.10: 두 줄 레이아웃으로 변경
 */
function ProjectTagsDisplay({
  project,
  tagMap,
}: {
  project: ProjectWithRevision;
  tagMap: Record<string, ProjectTagMaster>;
}) {
  const workTypeTags = project.workTypeTags || [];
  const domainTags = project.projectDomainTags || [];

  // 태그가 없으면 null 반환
  if (workTypeTags.length === 0 && domainTags.length === 0) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
      {/* 윗줄: 업무형태 태그 */}
      {workTypeTags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', justifyContent: 'center' }}>
          {workTypeTags.map((tagId) => {
            const tag = tagMap[tagId];
            if (!tag) return null;
            return (
              <span
                key={tagId}
                style={{
                  padding: '1px 6px',
                  fontSize: '10px',
                  borderRadius: '8px',
                  background: '#eef2ff',
                  color: '#4338ca',
                }}
              >
                {tag.label}
              </span>
            );
          })}
        </div>
      )}
      {/* 아랫줄: 프로젝트 분야 태그 */}
      {domainTags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', justifyContent: 'center' }}>
          {domainTags.map((tagId) => {
            const tag = tagMap[tagId];
            if (!tag) return null;
            return (
              <span
                key={tagId}
                style={{
                  padding: '1px 6px',
                  fontSize: '10px',
                  borderRadius: '8px',
                  background: '#ecfdf5',
                  color: '#047857',
                }}
              >
                {tag.label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function OrgProjectsPage() {
  // 스코프 조회
  const {
    isLoading: scopeLoading,
    error: scopeError,
    scopeOrgUnitIds,
    scopeMode,
    requester,
  } = useOrgManagementScope();

  // V91.3: 권한 체크 (태그 탭 표시 여부)
  const { user, hasRole, isLoading: authLoading } = useAuthorization();

  // V91.3: 태그 마스터 접근 권한 확인
  const canAccessTagMaster = useMemo(() => {
    if (authLoading || !user) return false;

    // 1. userType === 'admin'
    const isAdminUser = (user?.profile as any)?.userType === 'admin';
    if (isAdminUser) return true;

    // 2. Whitelist 방식 - 허용 역할 중 하나라도 있으면 통과
    return TAG_MASTER_ALLOWED_ROLES.some(roleId => hasRole(roleId));
  }, [user, hasRole, authLoading]);

  // V97: 고객사 마스터 접근 권한 (태그와 동일)
  const canAccessCustomerMaster = canAccessTagMaster;

  // V91.3: 페이지 탭 상태 (projects / tags / customers)
  const [pageTab, setPageTab] = useState<PageTab>('projects');

  // V97: 고객사 마스터 훅
  const {
    customers: customerMasters,
    isLoading: customersLoading,
    refetch: refetchCustomers,
  } = useCustomers({ includeArchived: false });
  const {
    createCustomer,
    updateCustomer,
    archiveCustomer,
    isLoading: customerMutationLoading,
  } = useCustomerMutations();

  // V97: 고객사 ID → 객체 매핑
  const customerMap = useMemo(() => {
    const map: Record<string, Customer> = {};
    for (const c of customerMasters) {
      map[c.id] = c;
    }
    return map;
  }, [customerMasters]);

  // V95: 조직명 매핑을 위한 조직 목록 조회 (모든 인증 사용자 접근 가능)
  const { orgUnits } = useOrgUnitsPublic({ isActive: true });

  // V235: MM마스터 설정 조회 권한 = admin-like OR role_exec (BE checkMmMasterAccess 동일 조건)
  const canAccessMmMaster = useMemo(() => {
    if (authLoading || !user) return false;
    if ((user?.profile as any)?.userType === 'admin') return true;
    return [...ADMIN_LIKE_ROLES, 'role_exec'].some(r => hasRole(r));
  }, [user, hasRole, authLoading]);

  // V54.11: MM마스터 On 조직 필터용 데이터 조회
  // V235: 권한 없는 역할(role_manager)은 호출 스킵 → 403 방지
  const { orgUnits: mmOrgUnits, isLoading: mmOrgLoading } = useMmOrgSettings({ enabled: canAccessMmMaster });

  // 조직 ID → 조직명 매핑
  const orgNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const org of orgUnits) {
      map[org.id] = org.name;
    }
    return map;
  }, [orgUnits]);

  const isScopeReady = !scopeLoading && scopeOrgUnitIds.length > 0;

  // V91.1: 페이지 레벨 태그 캐시 (목록 표시용)
  const [pageTagMap, setPageTagMap] = useState<Record<string, ProjectTagMaster>>({});
  const { listTags: pageListTags } = useProjectTags();

  // V91.1: 태그 마스터 로드 (페이지 레벨 캐시)
  useEffect(() => {
    const loadPageTags = async () => {
      try {
        const tags = await pageListTags();
        const map: Record<string, ProjectTagMaster> = {};
        for (const tag of tags) {
          map[tag.id] = tag;
        }
        setPageTagMap(map);
      } catch (err) {
        // tag cache load failed silently
      }
    };
    loadPageTags();
  }, [pageListTags]);

  // 검색 및 필터
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | ''>('active'); // V99.9: 기본값 진행중

  // V69 (2026-05-20/05-21): 국가 + 조직 필터 — 최초 진입 시 본인 국가/조직 1회 자동 적용.
  //   - 국가 필터: KR / CN / ALL (default ALL → currentUser 로딩 후 본인 companyCode 로 1회 set)
  //   - 조직 필터: 본인 orgUnitId (currentUser + orgUnits 둘 다 로딩 후 1회 set)
  //   - 사용자가 직접 변경하면 자동 덮어쓰기 금지 (initialDefaultsAppliedRef 가드).
  //   - "모든 프로젝트 보기" 클릭 시 → 국가 ALL / 조직 전체 reset (resetAllFilters).
  const { user: currentUser } = useCurrentUser();
  const userCompanyCode = (currentUser?.profile?.companyCode as 'KR' | 'CN' | undefined) ?? undefined;
  const userOrgUnitId = (currentUser?.profile?.orgUnitId as string | undefined) ?? undefined;
  const [selectedCompanyCode, setSelectedCompanyCode] = useState<'KR' | 'CN' | 'ALL'>('ALL');

  // V54.11: 조직 필터 (MM마스터 On 조직만 옵션) - 빈 문자열 = 전체
  const [selectedOrgUnitId, setSelectedOrgUnitId] = useState<string>('');
  // V54.11: 고객사 필터 (진행중 프로젝트의 고객사만 옵션) - 빈 문자열 = 전체
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>('');

  // V102: 태그 필터 상태
  const [selectedWorkTypeTagIds, setSelectedWorkTypeTagIds] = useState<string[]>([]);
  const [selectedProjectDomainTagIds, setSelectedProjectDomainTagIds] = useState<string[]>([]);

  // V102: 태그 마스터 리스트 (pageTagMap에서 분류)
  const workTypeTagMasterList = useMemo(() => {
    return Object.values(pageTagMap)
      .filter(tag => tag.type === 'workType')
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }, [pageTagMap]);

  const projectDomainTagMasterList = useMemo(() => {
    return Object.values(pageTagMap)
      .filter(tag => tag.type === 'projectDomain')
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }, [pageTagMap]);

  // V54.11: MM마스터 On 조직 필터 옵션 (enabled=true인 조직만, 이름순 정렬)
  const mmEnabledOrgOptions = useMemo(() => {
    return mmOrgUnits
      .filter(org => org.enabled)
      .map(org => ({ id: org.orgUnitId, name: org.name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  }, [mmOrgUnits]);

  // V69 (2026-05-21): cross-country reader 판별 (BE checkProjectReadPermission 와 동일 정책)
  //   - admin-like: system_admin / role_admin_kr / role_admin_cn
  //   - manager/exec: role_manager / role_exec
  //   - 그 외: 기존 본인 scope 만
  const isCrossCountryReader = useMemo(() => {
    if (!currentUser) return false;
    const roles = currentUser.roles || [];
    const isAdminLike = roles.some(r => (ADMIN_LIKE_ROLES as readonly string[]).includes(r));
    const isManagerOrExec = roles.includes('role_manager') || roles.includes('role_exec');
    return isAdminLike || isManagerOrExec;
  }, [currentUser]);

  // V69 (2026-05-21): orgUnitId → root org companyCode 추론 헬퍼 (parent chain walk-up).
  //   useOrgUnitsPublic 의 전체 orgUnits 가 source. root 는 parentId=null + companyCode 보유.
  const orgIdToCompanyCode = useMemo(() => {
    const idMap = new Map<string, any>();
    for (const o of orgUnits) idMap.set(o.id, o);
    const cache = new Map<string, 'KR' | 'CN' | null>();
    const resolve = (orgId: string): 'KR' | 'CN' | null => {
      if (cache.has(orgId)) return cache.get(orgId)!;
      let cur: string | null = orgId;
      let depth = 0;
      while (cur && depth < 10) {
        const o = idMap.get(cur);
        if (!o) { cache.set(orgId, null); return null; }
        if (o.companyCode === 'KR' || o.companyCode === 'CN') {
          cache.set(orgId, o.companyCode);
          return o.companyCode;
        }
        cur = o.parentId || null;
        depth++;
      }
      cache.set(orgId, null);
      return null;
    };
    return resolve;
  }, [orgUnits]);

  // V69 (2026-05-21): 조직 필터 옵션 — cross-country reader 는 선택 국가의 전체 활성 조직.
  //   일반 사용자는 기존 MM마스터 On 조직 (mmEnabledOrgOptions).
  //   국가 = ALL 일 때: KR + CN 전체 조직 노출 (사용자가 특정 조직을 골라 subtree 필터 적용 가능).
  const orgFilterOptions = useMemo(() => {
    if (!isCrossCountryReader) return mmEnabledOrgOptions;
    return orgUnits
      .filter(org => {
        if (selectedCompanyCode === 'ALL') return true;
        return orgIdToCompanyCode(org.id) === selectedCompanyCode;
      })
      .map(org => ({ id: org.id, name: org.name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  }, [isCrossCountryReader, orgUnits, selectedCompanyCode, orgIdToCompanyCode, mmEnabledOrgOptions]);

  // V69 (2026-05-21): 최초 진입 시 본인 국가 + 본인 조직 자동 적용 (1회 only).
  //   - currentUser + orgUnits 둘 다 로딩 완료 후 발동
  //   - 본인 orgUnitId 가 orgUnits 에 존재할 때만 set (방어)
  //   - 이후 사용자가 직접 필터 변경한 값은 절대 덮어쓰지 않음 (ref guard)
  //   - "모든 프로젝트 보기" 클릭 시에는 resetAllFilters 가 ALL/전체 로 강제 reset (의도된 동작)
  const initialDefaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (initialDefaultsAppliedRef.current) return;
    if (!currentUser?.profile) return;
    if (orgUnits.length === 0) return;
    if (userCompanyCode) {
      setSelectedCompanyCode(userCompanyCode);
    }
    if (userOrgUnitId && orgUnits.some(o => o.id === userOrgUnitId)) {
      setSelectedOrgUnitId(userOrgUnitId);
    }
    initialDefaultsAppliedRef.current = true;
  }, [currentUser, orgUnits, userCompanyCode, userOrgUnitId]);

  // V69 (2026-05-21): 사용자가 국가 필터를 직접 변경하면 조직 필터 reset.
  //   초기 자동 적용 (위 useEffect) 과 분리하여 자동 적용이 조직 필터를 덮어쓰지 않도록.
  const handleCompanyCodeChange = useCallback((newCode: 'KR' | 'CN' | 'ALL') => {
    setSelectedCompanyCode(newCode);
    setSelectedOrgUnitId('');
  }, []);

  // 프로젝트 조회
  const {
    projects,
    selectedProject,
    setSelectedProject,
    revisionHistory,
    isLoading,
    isLoadingDetail,
    isMutating,
    error,
    refetch,
    fetchProjectHistory,
    createProject,
    updateProjectMaster,
    editProjectRevision,
    renewProject,
    deleteProjects,
    // V83: 예약 갱신 함수
    scheduleProjectRenewal,
    cancelProjectRenewalReservation,
    getProjectRenewalReservations,
    debugMeta,
  } = useProjects({
    scopeOrgUnitIds,
    status: statusFilter || undefined,
    search: searchQuery || undefined,
    // V69 (2026-05-20): 국가 필터. ALL 은 BE 가 undefined 처리.
    companyCode: selectedCompanyCode,
    enabled: isScopeReady,
  });

  // V69 (2026-05-21): WRITE 권한 판정 — listProjects 가 KR/CN 전사 read 일 수 있어도
  //   편집/삭제/갱신 권한은 본인 BE-side scope (useOrgManagementScope.scopeOrgUnitIds) 안의 프로젝트로 제한.
  //   admin-like / CEO 는 scopeMode='ALL' → 전 조직 포함되어 자동 통과.
  const writeScopeOrgUnitSet = useMemo(() => new Set(scopeOrgUnitIds), [scopeOrgUnitIds]);
  const canWriteProject = useCallback(
    (proj: { ownerOrgUnitId?: string } | undefined | null): boolean => {
      if (!proj?.ownerOrgUnitId) return false;
      return writeScopeOrgUnitSet.has(proj.ownerOrgUnitId);
    },
    [writeScopeOrgUnitSet]
  );

  // V104: 페이지네이션 상수 및 상태
  const ITEMS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // V73.2: 조직 subtree 필터 (공통 훅)
  const { descendantOrgUnitIdsSet: descendantOrgIdsSet } = useOrgUnitSubtreeFilter(orgUnits, selectedOrgUnitId);

  // V69 (2026-05-20): 프로젝트 분류 필터 — 'all' | 'current' | 'historical'
  // (filteredProjects useMemo 내부에서 사용되므로 그 전에 선언되어야 함)
  const [historicalFilter, setHistoricalFilter] = useState<'all' | 'current' | 'historical'>('all');

  // V69 (2026-05-20): 필터 초기화 — 검색어/상태/국가/조직/고객사/태그/분류/페이지 모두 default 로
  const resetAllFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('active');
    setSelectedCompanyCode('ALL');
    setSelectedOrgUnitId('');
    setSelectedCustomerName('');
    setSelectedWorkTypeTagIds([]);
    setSelectedProjectDomainTagIds([]);
    setHistoricalFilter('all');
    setCurrentPage(1);
  }, []);

  // V54.12.1: Step1 - 조직 필터 적용한 base 후보 (고객사 옵션 산출 기반)
  // V73: 조직 필터 시 하위조직 소속 프로젝트도 포함
  const baseCandidateProjects = useMemo(() => {
    return projects.filter(project => {
      // 조직 필터 (MM마스터 On 조직 + 하위조직)
      if (descendantOrgIdsSet) {
        if (!descendantOrgIdsSet.has(project.ownerOrgUnitId || '')) return false;
      }
      return true;
    });
  }, [projects, descendantOrgIdsSet]);

  // V54.12.1: Step2 - base 후보에서 고객사 옵션 추출 (정합성 보장)
  // 조직 필터가 적용된 프로젝트에서만 고객사 추출 → 0건 불일치 방지
  const activeProjectCustomerOptions = useMemo(() => {
    const customerNames = new Set<string>();
    for (const proj of baseCandidateProjects) {
      const name = proj.currentRevision?.customer?.name;
      if (name && name.trim()) {
        customerNames.add(name.trim());
      }
    }
    return Array.from(customerNames).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [baseCandidateProjects]);

  // V54.12.1: Step3 - base 후보에 고객사/태그 필터 적용
  const filteredProjects = useMemo(() => {
    const filtered = baseCandidateProjects.filter(project => {
      // 고객사 필터
      if (selectedCustomerName) {
        const projectCustomerName = project.currentRevision?.customer?.name?.trim() || '';
        if (projectCustomerName !== selectedCustomerName) return false;
      }

      // 업무형태 태그 필터 (AND 조건: 선택된 모든 태그를 포함해야 함)
      if (selectedWorkTypeTagIds.length > 0) {
        const projectTags = project.workTypeTags || [];
        const hasAllWorkTypeTags = selectedWorkTypeTagIds.every(tagId => projectTags.includes(tagId));
        if (!hasAllWorkTypeTags) return false;
      }

      // 프로젝트 분야 태그 필터 (AND 조건)
      if (selectedProjectDomainTagIds.length > 0) {
        const projectTags = project.projectDomainTags || [];
        const hasAllDomainTags = selectedProjectDomainTagIds.every(tagId => projectTags.includes(tagId));
        if (!hasAllDomainTags) return false;
      }

      // V69 (2026-05-20): 과거 프로젝트 필터 (전체/일반/과거)
      const isHistorical = (project as any).isHistorical === true;
      if (historicalFilter === 'current' && isHistorical) return false;
      if (historicalFilter === 'historical' && !isHistorical) return false;

      return true;
    });

    // V104: 최신 등록순 정렬 (createdAt DESC)
    return filtered.sort((a, b) => {
      const getTime = (ts: any): number => {
        if (!ts) return 0;
        if (typeof ts.toMillis === 'function') return ts.toMillis();
        if (typeof ts.seconds === 'number') return ts.seconds * 1000;
        if (ts instanceof Date) return ts.getTime();
        return 0;
      };
      return getTime(b.createdAt) - getTime(a.createdAt);
    });
  }, [baseCandidateProjects, selectedCustomerName, selectedWorkTypeTagIds, selectedProjectDomainTagIds, historicalFilter]);

  // V104: 페이지네이션 계산
  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProjects.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProjects, currentPage]);

  // V104: 필터 변경 시 페이지 초기화 + V54.11: 조직/고객사 필터 추가
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery, selectedOrgUnitId, selectedCustomerName, selectedWorkTypeTagIds, selectedProjectDomainTagIds]);

  // V54.12.1: 조직 필터 변경 시 고객사 필터 초기화 (정합성 유지)
  // 조직 A → 조직 B 변경 시, 조직 B에 없는 고객사가 선택되어 있으면 0건 발생 방지
  useEffect(() => {
    // 현재 선택된 고객사가 새로운 baseCandidateProjects에 없으면 초기화
    if (selectedCustomerName && !activeProjectCustomerOptions.includes(selectedCustomerName)) {
      setSelectedCustomerName('');
    }
  }, [activeProjectCustomerOptions, selectedCustomerName]);

  // 모달/Drawer 상태
  // V69 (2026-05-20): 'historical' 모드 추가 — 과거 프로젝트 생성
  const [showCreateModal, setShowCreateModal] = useState<false | 'normal' | 'historical'>(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // 전체 로딩 상태
  const isPageLoading = scopeLoading || isLoading;

  // 에러 메시지
  const errorMessage = scopeError?.message || error?.message;

  // 프로젝트 선택 핸들러 (Drawer 오픈 + 이력 조회)
  const handleSelectProject = useCallback((project: ProjectWithRevision) => {
    setSelectedProject(project);
    setShowDrawer(true);
    fetchProjectHistory(project.id);
  }, [setSelectedProject, fetchProjectHistory]);

  // Drawer 닫기
  const handleCloseDrawer = useCallback(() => {
    setShowDrawer(false);
    setSelectedProject(null);
  }, [setSelectedProject]);

  // 프로젝트 생성 핸들러
  // V69 (2026-05-20): historical mode 시 isHistorical=true + staffAssignments=[] 강제
  const handleCreateProject = useCallback(async (data: CreateProjectRequest, historicalMode?: boolean) => {
    const payload: CreateProjectRequest = historicalMode
      ? {
          ...data,
          isHistorical: true,
          initialRevision: {
            ...data.initialRevision,
            staffAssignments: [],
          },
        }
      : data;
    const result = await createProject(payload);
    if (result.success) {
      setShowCreateModal(false);
    } else {
      alert(result.error || '프로젝트 생성에 실패했습니다.');
    }
  }, [createProject]);

  // 선택 삭제 핸들러
  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;

    if (!window.confirm(`${selectedIds.length}개의 프로젝트를 삭제하시겠습니까?`)) {
      return;
    }

    const result = await deleteProjects(selectedIds);
    if (result.success) {
      setSelectedIds([]);
    } else {
      alert(result.error || '프로젝트 삭제에 실패했습니다.');
    }
  }, [selectedIds, deleteProjects]);

  // 체크박스 토글
  const toggleSelectId = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  // 전체 선택 (V104: paginatedProjects 기준 - 현재 페이지만)
  const toggleSelectAll = useCallback(() => {
    const currentPageIds = paginatedProjects.map((p) => p.id);
    const allSelected = currentPageIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !currentPageIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...currentPageIds])));
    }
  }, [selectedIds, paginatedProjects]);

  // 수정 저장 핸들러
  const handleEditSave = useCallback(async (
    projectId: string,
    revisionId: string,
    patch: Partial<ProjectRevision>
  ) => {
    const result = await editProjectRevision({
      projectId,
      revisionId,
      patch,
    });
    if (result.success) {
      // 목록과 상세 새로고침
      await fetchProjectHistory(projectId);
    } else {
      alert(result.error || '수정에 실패했습니다.');
    }
    return result;
  }, [editProjectRevision, fetchProjectHistory]);

  // 갱신 핸들러
  // V100: status 파라미터 추가
  const handleRenew = useCallback(async (
    projectId: string,
    newRevision: Omit<ProjectRevision, 'id' | 'projectId' | 'revisionNo' | 'createdAt' | 'createdBy'>,
    status?: ProjectStatus
  ) => {
    const result = await renewProject({
      projectId,
      newRevision,
      // V100: status가 있으면 포함
      ...(status && { status }),
    });
    if (result.success) {
      await fetchProjectHistory(projectId);
    } else {
      alert(result.error || '갱신에 실패했습니다.');
    }
    return result;
  }, [renewProject, fetchProjectHistory]);

  // 마스터 수정 핸들러
  const handleUpdateMaster = useCallback(async (
    projectId: string,
    patch: Partial<Pick<ProjectWithRevision, 'name' | 'status' | 'description'>>
  ) => {
    const result = await updateProjectMaster({
      projectId,
      patch,
    });
    if (result.success) {
      await fetchProjectHistory(projectId);
    } else {
      alert(result.error || '마스터 정보 수정에 실패했습니다.');
    }
    return result;
  }, [updateProjectMaster, fetchProjectHistory]);

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: '#f8fafc' }}>
      {/* 헤더 */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>프로젝트 관리</h1>
        </div>
        {scopeMode && pageTab === 'projects' && (
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
            스코프: {SCOPE_MODE_LABELS[scopeMode]} ({scopeOrgUnitIds.length}개 조직)
          </p>
        )}
        {/* V68: 프로젝트 생성/수정 가이드 안내 (프로젝트 목록 탭에서만) */}
        {pageTab === 'projects' && (
          <div style={{ marginTop: '6px', color: '#94a3b8', fontSize: '13px', lineHeight: 1.6 }}>
            <p style={{ margin: 0 }}>· 고객사와 태그를 먼저 등록한 후 프로젝트를 생성해주세요.</p>
            <p style={{ margin: 0 }}>· 프로젝트 정보가 변경될 경우 프로젝트 상세 편집을 통해 리비전을 생성해주세요.</p>
          </div>
        )}
      </div>

      {/* V91.3: 페이지 탭 (프로젝트 목록 / 태그 마스터) */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '20px',
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: '0',
        }}
      >
        <button
          onClick={() => setPageTab('projects')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderBottom: pageTab === 'projects' ? '2px solid #4f46e5' : '2px solid transparent',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: pageTab === 'projects' ? 600 : 400,
            color: pageTab === 'projects' ? '#4f46e5' : '#64748b',
            transition: 'all 0.15s',
          }}
        >
          프로젝트 목록
        </button>
        {canAccessTagMaster && (
          <button
            onClick={() => setPageTab('tags')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: pageTab === 'tags' ? '2px solid #4f46e5' : '2px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: pageTab === 'tags' ? 600 : 400,
              color: pageTab === 'tags' ? '#4f46e5' : '#64748b',
              transition: 'all 0.15s',
            }}
          >
            태그 마스터
          </button>
        )}
        {/* V97: 고객사 마스터 탭 버튼 */}
        {canAccessCustomerMaster && (
          <button
            onClick={() => setPageTab('customers')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: pageTab === 'customers' ? '2px solid #4f46e5' : '2px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: pageTab === 'customers' ? 600 : 400,
              color: pageTab === 'customers' ? '#4f46e5' : '#64748b',
              transition: 'all 0.15s',
            }}
          >
            고객사 마스터
          </button>
        )}
      </div>

      {/* V91.3: 태그 마스터 탭 */}
      {pageTab === 'tags' && canAccessTagMaster && (
        <div style={{ maxWidth: '800px', margin: '0 auto', minHeight: '560px' }}>
          <ProjectTagMasterPanel
            onTagsChange={async () => {
              // 태그 변경 시 페이지 캐시 갱신
              try {
                const tags = await pageListTags();
                const map: Record<string, ProjectTagMaster> = {};
                for (const tag of tags) {
                  map[tag.id] = tag;
                }
                setPageTagMap(map);
              } catch (err) {
                // tag cache update failed silently
              }
            }}
          />
        </div>
      )}

      {/* V97: 고객사 마스터 탭 */}
      {pageTab === 'customers' && canAccessCustomerMaster && (
        <div style={{ maxWidth: '800px', margin: '0 auto', minHeight: '560px' }}>
          <CustomerMasterPanel
            customers={customerMasters}
            isLoading={customersLoading}
            onCreate={async (input) => {
              await createCustomer(input);
              await refetchCustomers();
            }}
            onUpdate={async (input) => {
              await updateCustomer(input);
              await refetchCustomers();
            }}
            onArchive={async (id) => {
              await archiveCustomer(id);
              await refetchCustomers();
            }}
            isMutating={customerMutationLoading}
            // V98: 프로젝트 클릭 → Drawer 열기
            onOpenProject={(projectId) => {
              // 현재 목록에서 프로젝트 찾기
              const targetProject = projects.find((p) => p.id === projectId);
              if (targetProject) {
                // 프로젝트 탭으로 이동 후 Drawer 열기
                setPageTab('projects');
                handleSelectProject(targetProject);
              } else {
                // 목록에 없으면 프로젝트 탭으로 이동하고 안내
                setPageTab('projects');
                alert('해당 프로젝트를 찾을 수 없습니다. 조직 스코프를 확인해주세요.');
              }
            }}
          />
        </div>
      )}

      {/* V91.3: 프로젝트 목록 탭 */}
      {pageTab === 'projects' && (
        <>
          {/* 에러 표시 */}
      {errorMessage && (
        <div
          style={{
            padding: '16px',
            marginBottom: '16px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#dc2626',
          }}
        >
          <AlertCircle size={20} />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* 도구 모음 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        {/* 왼쪽: 검색 + 필터
            V69 (2026-05-20): 국가 필터 + 필터 초기화 추가로 폭이 늘어남 → padding/gap/width 일괄 compact 화.
            flexWrap=wrap 으로 좁은 화면에서는 자연스럽게 줄바꿈, 일반 데스크탑(≥1440px)에서는 1줄 유지. */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* 검색 */}
          <div style={{ position: 'relative' }}>
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
              }}
            />
            <input
              type="text"
              placeholder="프로젝트명/코드 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '6px 10px 6px 32px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                width: '180px',
                height: '32px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* 상태 필터 */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ProjectStatus | '')}
            style={{
              padding: '6px 8px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              background: 'white',
              height: '32px',
              boxSizing: 'border-box',
            }}
          >
            <option value="">전체 상태</option>
            <option value="active">진행중</option>
            <option value="completed">완료</option>
            <option value="onHold">중단</option>
            <option value="cancelled">취소</option>
          </select>

          {/* V69 (2026-05-20): 국가 필터 — KR / CN / ALL.
              기본값: 사용자 companyCode (KR 직원 → KR, CN 직원 → CN).
              ALL 선택 시 BE 가 caller scope + role 기준으로 안전 처리 (manager/exec → KR+CN 전체 read). */}
          <select
            value={selectedCompanyCode}
            onChange={(e) => handleCompanyCodeChange(e.target.value as 'KR' | 'CN' | 'ALL')}
            style={{
              padding: '6px 8px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              background: 'white',
              minWidth: '78px',
              height: '32px',
              boxSizing: 'border-box',
            }}
            title="국가 필터 — 권한 없는 사용자가 ALL 을 선택해도 BE 가 본인 회사 외 데이터를 차단합니다."
          >
            <option value="ALL">ALL</option>
            <option value="KR">KR</option>
            <option value="CN">CN</option>
          </select>

          {/* V54.11: 조직 필터 (MM마스터 On 조직만) */}
          <select
            value={selectedOrgUnitId}
            onChange={(e) => setSelectedOrgUnitId(e.target.value)}
            disabled={mmOrgLoading}
            style={{
              padding: '6px 8px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              background: 'white',
              minWidth: '120px',
              height: '32px',
              boxSizing: 'border-box',
            }}
          >
            <option value="">전체 조직</option>
            {orgFilterOptions.map(org => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>

          {/* V54.11: 고객사 필터 (진행중 프로젝트의 고객사만) */}
          <select
            value={selectedCustomerName}
            onChange={(e) => setSelectedCustomerName(e.target.value)}
            style={{
              padding: '6px 8px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              background: 'white',
              minWidth: '120px',
              height: '32px',
              boxSizing: 'border-box',
            }}
          >
            <option value="">전체 고객사</option>
            {activeProjectCustomerOptions.map(name => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          {/* V102: 업무형태 태그 필터 */}
          <div style={{ position: 'relative' }}>
            <select
              value=""
              onChange={(e) => {
                const tagId = e.target.value;
                if (tagId && !selectedWorkTypeTagIds.includes(tagId)) {
                  setSelectedWorkTypeTagIds([...selectedWorkTypeTagIds, tagId]);
                }
              }}
              style={{
                padding: '6px 8px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'white',
                minWidth: '120px',
                height: '32px',
                boxSizing: 'border-box',
              }}
            >
              <option value="">업무형태 {selectedWorkTypeTagIds.length > 0 ? `(${selectedWorkTypeTagIds.length})` : ''}</option>
              {workTypeTagMasterList
                .filter(tag => tag.status === 'active' || selectedWorkTypeTagIds.includes(tag.id))
                .map(tag => (
                  <option
                    key={tag.id}
                    value={tag.id}
                    disabled={selectedWorkTypeTagIds.includes(tag.id)}
                  >
                    {tag.label}{tag.status === 'archived' ? ' (비활성)' : ''}
                    {selectedWorkTypeTagIds.includes(tag.id) ? ' ✓' : ''}
                  </option>
                ))}
            </select>
            {selectedWorkTypeTagIds.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px', maxWidth: '200px' }}>
                {selectedWorkTypeTagIds.map(tagId => {
                  const tag = pageTagMap[tagId];
                  return (
                    <span
                      key={tagId}
                      style={{
                        padding: '2px 6px',
                        fontSize: '11px',
                        borderRadius: '8px',
                        background: '#eef2ff',
                        color: '#4338ca',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {tag?.label || tagId}
                      <button
                        type="button"
                        onClick={() => setSelectedWorkTypeTagIds(selectedWorkTypeTagIds.filter(id => id !== tagId))}
                        style={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: '12px',
                          color: '#4338ca',
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* V102: 프로젝트 분야 태그 필터 */}
          <div style={{ position: 'relative' }}>
            <select
              value=""
              onChange={(e) => {
                const tagId = e.target.value;
                if (tagId && !selectedProjectDomainTagIds.includes(tagId)) {
                  setSelectedProjectDomainTagIds([...selectedProjectDomainTagIds, tagId]);
                }
              }}
              style={{
                padding: '6px 8px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'white',
                minWidth: '120px',
                height: '32px',
                boxSizing: 'border-box',
              }}
            >
              <option value="">프로젝트 분야 {selectedProjectDomainTagIds.length > 0 ? `(${selectedProjectDomainTagIds.length})` : ''}</option>
              {projectDomainTagMasterList
                .filter(tag => tag.status === 'active' || selectedProjectDomainTagIds.includes(tag.id))
                .map(tag => (
                  <option
                    key={tag.id}
                    value={tag.id}
                    disabled={selectedProjectDomainTagIds.includes(tag.id)}
                  >
                    {tag.label}{tag.status === 'archived' ? ' (비활성)' : ''}
                    {selectedProjectDomainTagIds.includes(tag.id) ? ' ✓' : ''}
                  </option>
                ))}
            </select>
            {selectedProjectDomainTagIds.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px', maxWidth: '200px' }}>
                {selectedProjectDomainTagIds.map(tagId => {
                  const tag = pageTagMap[tagId];
                  return (
                    <span
                      key={tagId}
                      style={{
                        padding: '2px 6px',
                        fontSize: '11px',
                        borderRadius: '8px',
                        background: '#ecfdf5',
                        color: '#047857',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {tag?.label || tagId}
                      <button
                        type="button"
                        onClick={() => setSelectedProjectDomainTagIds(selectedProjectDomainTagIds.filter(id => id !== tagId))}
                        style={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: '12px',
                          color: '#047857',
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* V69 (2026-05-20): 필터 해제 + KR/CN 전체 보기.
              동작: resetAllFilters (검색/상태/국가/조직/고객사/태그/분류/페이지 모두 default 로).
              라벨: "모든 프로젝트 보기" — 단순 reset 보다 "필터 해제 + 전체 조회" 의도가 직관적. */}
          <button
            onClick={resetAllFilters}
            style={{
              padding: '6px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: 'white',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              height: '32px',
              boxSizing: 'border-box',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
            title="모든 필터를 해제하고 권한 범위 내 전체 프로젝트를 표시합니다."
          >
            <RotateCcw size={14} />
            <span>모든 프로젝트 보기</span>
          </button>
        </div>

        {/* 오른쪽: 액션 버튼 */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => refetch()}
            disabled={isPageLoading}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              background: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <RefreshCw size={16} className={isPageLoading ? 'animate-spin' : ''} />
            새로고침
          </button>

          {selectedIds.length > 0 && (() => {
            // V69 (2026-05-21): 선택된 모든 프로젝트가 본인 WRITE scope 안일 때만 삭제 활성화.
            //   타 조직/회사 프로젝트가 포함되면 disabled — BE 도 차단하지만 UX 가드 우선.
            const allWritable = selectedIds.every((id) => {
              const proj = projects.find((p) => p.id === id);
              return proj ? canWriteProject(proj) : false;
            });
            return (
              <button
                onClick={handleDeleteSelected}
                disabled={isMutating || !allWritable}
                title={allWritable ? '선택된 프로젝트를 삭제합니다.' : '본인 권한 범위 밖 프로젝트가 포함되어 있어 삭제할 수 없습니다.'}
                style={{
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: '8px',
                  background: allWritable ? '#ef4444' : '#fca5a5',
                  color: 'white',
                  cursor: (isMutating || !allWritable) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: allWritable ? 1 : 0.7,
                }}
              >
                <Trash2 size={16} />
                삭제 ({selectedIds.length})
              </button>
            );
          })()}

          <button
            onClick={() => setShowCreateModal('normal')}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '8px',
              background: '#4f46e5',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 500,
            }}
          >
            <Plus size={16} />
            새 프로젝트
          </button>

          {/* V69 (2026-05-20): 프로젝트 이력 (Historical) — 실 직원 매핑 없이 등급별 인원수만 입력. isHistorical=true 로 저장. */}
          <button
            onClick={() => setShowCreateModal('historical')}
            style={{
              padding: '8px 16px',
              border: '1px solid #4f46e5',
              borderRadius: '8px',
              background: 'white',
              color: '#4f46e5',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 500,
            }}
          >
            <Plus size={16} />
            프로젝트 이력
          </button>
        </div>
      </div>

      {/* V69 (2026-05-20): 프로젝트 분류 필터 토글 */}
      <div style={{ marginTop: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <span style={{ color: '#6b7280' }}>분류:</span>
        {(['all', 'current', 'historical'] as const).map((opt) => (
          <button
            key={opt}
            onClick={() => setHistoricalFilter(opt)}
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              border: `1px solid ${historicalFilter === opt ? '#4f46e5' : '#d1d5db'}`,
              background: historicalFilter === opt ? '#4f46e5' : 'white',
              color: historicalFilter === opt ? 'white' : '#374151',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {opt === 'all' ? '전체' : opt === 'current' ? '일반' : 'Done'}
          </button>
        ))}
      </div>

      {/* 프로젝트 테이블 */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', width: '40px' }}>
                <input
                  type="checkbox"
                  checked={paginatedProjects.length > 0 && paginatedProjects.every(p => selectedIds.includes(p.id))}
                  onChange={toggleSelectAll}
                />
              </th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: '13px', width: '180px' }}>
                프로젝트명
              </th>
              <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, fontSize: '13px' }}>
                담당조직
              </th>
              <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, fontSize: '13px' }}>
                고객사
              </th>
              <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, fontSize: '13px' }}>
                계약기간
              </th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: '13px' }}>
                계약(MM)
              </th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: '13px' }}>
                실투입(MM)
              </th>
              <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, fontSize: '13px' }}>
                비고
              </th>
              <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, fontSize: '13px', width: '100px' }}>
                상태
              </th>
              <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, fontSize: '13px' }}>
                태그
              </th>
            </tr>
          </thead>
          <tbody>
            {isPageLoading ? (
              <tr>
                <td colSpan={10} style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
                  로딩 중...
                </td>
              </tr>
            ) : filteredProjects.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
                  {isScopeReady ? '프로젝트가 없습니다.' : '스코프를 불러오는 중...'}
                </td>
              </tr>
            ) : (
              paginatedProjects.map((project) => (
                <tr
                  key={project.id}
                  onClick={() => handleSelectProject(project)}
                  style={{
                    cursor: 'pointer',
                    borderTop: '1px solid #f1f5f9',
                    background: selectedProject?.id === project.id ? '#f0f9ff' : undefined,
                  }}
                >
                  <td
                    style={{ padding: '10px 14px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(project.id)}
                      onChange={() => toggleSelectId(project.id)}
                    />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'left', fontSize: '14px' }}>
                    <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{project.name}</span>
                      {(project as any).isHistorical === true && (
                        <span
                          style={{
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            fontWeight: 500,
                            background: '#f3e8ff',
                            color: '#6b21a8',
                            border: '1px solid #c084fc',
                          }}
                          title="프로젝트 이력 (실투입 직원 배정 없이 등급별 인원수만 기록)"
                        >
                          Done
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: '14px' }}>
                    {/* V69 (2026-05-20): 프로젝트 이력(isHistorical=true) 은 조직 개념 없음 — '-' 표시.
                        내부 권한 scope 용 ownerOrgUnitId 는 저장되어 있지만 사용자에게 노출 금지. */}
                    {(project as any).isHistorical === true ? (
                      <span style={{ color: '#94a3b8' }}>-</span>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <Building2 size={14} style={{ color: '#94a3b8' }} />
                        {project.ownerOrgUnitNameSnapshot}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: '14px' }}>
                    {/* V99: customerId가 있으면 마스터명 우선, 없으면 스냅샷 fallback */}
                    {project.customerId && customerMap[project.customerId]
                      ? customerMap[project.customerId].name
                      : project.currentRevision?.customer?.name || '-'}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: '14px' }}>
                    {project.currentRevision?.contract ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <Calendar size={14} style={{ color: '#94a3b8' }} />
                        <span style={{ fontSize: '13px' }}>
                          {project.currentRevision.contract.startDate} ~ {project.currentRevision.contract.endDate}
                        </span>
                      </div>
                    ) : '-'}
                  </td>
                  {(() => {
                    // V99.5: 계약MM vs 실투입MM 비교 - 낮은 값 분홍색 표시
                    // V99.6: NaN/undefined 방어 - 유효 숫자일 때만 비교
                    const isValidNumber = (v: unknown): v is number =>
                      typeof v === 'number' && Number.isFinite(v);

                    const rawContractMM = project.currentRevision?.contract
                      ? getTotalContractedHeadcount(project.currentRevision.contract)
                      : undefined;
                    const rawActualMM = project.currentRevision
                      ? getTotalActualStaffing(project.currentRevision)
                      : undefined;

                    // 둘 다 유효 숫자일 때만 비교 가능
                    const canCompare = isValidNumber(rawContractMM) && isValidNumber(rawActualMM);
                    const isContractLower = canCompare && rawContractMM < rawActualMM;
                    const isActualLower = canCompare && rawActualMM < rawContractMM;

                    // 표시용 값: 유효하면 그대로, 아니면 0
                    const displayContractMM = isValidNumber(rawContractMM) ? rawContractMM : 0;
                    const displayActualMM = isValidNumber(rawActualMM) ? rawActualMM : 0;

                    const pinkStyle = { color: '#ec4899' };

                    return (
                      <>
                        <td style={{ padding: '10px 14px', textAlign: 'left', fontSize: '14px' }}>
                          {/* V89.1: 계약 MM + 등급 요약 (무 포함) */}
                          <span style={isContractLower ? pinkStyle : undefined}>
                            {formatMM(displayContractMM)} MM
                          </span>
                          <GradeBreakdownTokens
                            tokens={getGradeTokensForContract(project.currentRevision?.contract?.contractStaffing)}
                          />
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'left', fontSize: '14px' }}>
                          {/* V89.1: 실투입 MM + 등급 요약 (무 제외, 레거시 any→초급) */}
                          <span style={isActualLower ? pinkStyle : undefined}>
                            {formatMM(displayActualMM)} MM
                          </span>
                          <GradeBreakdownTokens
                            tokens={getGradeTokensForActual(project.currentRevision?.actualStaffing)}
                          />
                        </td>
                        {/* V101.1: 비고 컬럼 - 계약 메모(notes) 표시 */}
                        <td
                          style={{
                            padding: '10px 14px',
                            textAlign: 'center',
                            fontSize: '14px',
                            maxWidth: '280px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={project.currentRevision?.contract?.notes || ''}
                        >
                          {project.currentRevision?.contract?.notes || '-'}
                        </td>
                      </>
                    );
                  })()}
                  <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: '14px' }}>
                    <span
                      style={{
                        padding: '3px 8px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: 500,
                        background: getStatusColor(project.status).bg,
                        color: getStatusColor(project.status).text,
                      }}
                    >
                      {PROJECT_STATUS_LABELS[project.status]}
                    </span>
                  </td>
                  {/* V99.8: 태그 컬럼 - 가장 우측 */}
                  <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: '14px' }}>
                    <ProjectTagsDisplay project={project} tagMap={pageTagMap} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* V104: 페이지네이션 UI */}
        {filteredProjects.length > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderTop: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}
          >
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              총 {filteredProjects.length}개 중 {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredProjects.length)}개 표시
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  background: currentPage === 1 ? '#f1f5f9' : 'white',
                  color: currentPage === 1 ? '#94a3b8' : '#374151',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                }}
              >
                이전
              </button>
              <span style={{ fontSize: '13px', color: '#374151', minWidth: '80px', textAlign: 'center' }}>
                {currentPage} / {totalPages || 1}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  background: currentPage >= totalPages ? '#f1f5f9' : 'white',
                  color: currentPage >= totalPages ? '#94a3b8' : '#374151',
                  cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                }}
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 디버그 정보 */}
      {debugMeta && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            background: '#f0fdf4',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#166534',
          }}
        >
          조회 결과: {debugMeta.totalProjects}개 프로젝트 (스코프 {debugMeta.scopeCount}개 조직)
        </div>
      )}
        </>
      )}

      {/* 생성 모달 */}
      {showCreateModal && (
        <CreateProjectModal
          scopeOrgUnitIds={scopeOrgUnitIds}
          orgNameMap={orgNameMap}
          scopeMode={scopeMode || undefined}
          customerMasters={customerMasters}
          onClose={() => setShowCreateModal(false)}
          onCreate={(data) => handleCreateProject(data, showCreateModal === 'historical')}
          isLoading={isMutating}
          isHistoricalMode={showCreateModal === 'historical'}
        />
      )}

      {/* 프로젝트 상세 Drawer */}
      {showDrawer && selectedProject && (
        <ProjectDetailDrawer
          project={selectedProject}
          revisions={revisionHistory}
          isLoadingDetail={isLoadingDetail}
          isMutating={isMutating}
          onClose={handleCloseDrawer}
          onEditSave={handleEditSave}
          onRenew={handleRenew}
          onUpdateMaster={handleUpdateMaster}
          scopeOrgUnitIds={scopeOrgUnitIds}
          scopeMode={scopeMode || undefined}
          onGetReservations={getProjectRenewalReservations}
          onCancelReservation={cancelProjectRenewalReservation}
          onScheduleRenew={scheduleProjectRenewal}
          customerMap={customerMap}
          tagMap={pageTagMap}
          /* V69 (2026-05-21): WRITE 권한 — 본인 BE-side scope 안의 프로젝트만 편집 가능.
             타 조직/타 회사 프로젝트는 read-only (편집/갱신/예약/삭제 UI 모두 숨김). */
          readOnly={!canWriteProject(selectedProject)}
        />
      )}
    </div>
  );
}

/**
 * 상태별 색상
 */
function getStatusColor(status: ProjectStatus): { bg: string; text: string } {
  switch (status) {
    case 'active':
      return { bg: '#dcfce7', text: '#166534' };
    case 'completed':
      return { bg: '#dbeafe', text: '#1e40af' };
    case 'onHold':
      return { bg: '#fef9c3', text: '#854d0e' };
    case 'cancelled':
      return { bg: '#f3f4f6', text: '#374151' };
    default:
      return { bg: '#f3f4f6', text: '#374151' };
  }
}

// ============================================
// V69 (2026-05-20): 과거 프로젝트 등급별 인원수 단순 입력 컴포넌트
// - 직원/리더/외부/staffAssignments UI 없음.
// - GradeBasedFTE 5종(특급/고급/중급/초급/등급무관) 인원수만 입력.
// - MM 집계는 actualStaffing SSoT 그대로 사용.
// ============================================

interface HistoricalActualStaffingEditorProps {
  value: GradeBasedFTE;
  onChange: (next: GradeBasedFTE) => void;
  disabled?: boolean;
}

function HistoricalActualStaffingEditor({
  value,
  onChange,
  disabled = false,
}: HistoricalActualStaffingEditorProps) {
  const rows: { key: keyof GradeBasedFTE; label: string }[] = [
    { key: 'special', label: '특급' },
    { key: 'senior', label: '고급' },
    { key: 'mid', label: '중급' },
    { key: 'junior', label: '초급' },
    { key: 'any', label: '등급무관' },
  ];
  const total = rows.reduce((sum, r) => sum + (value[r.key] || 0), 0);
  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        padding: '12px',
        background: '#f9fafb',
        // V69 (2026-05-20): 좁은 모달에서도 overflow 방지
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#6b7280' }}>
        과거 프로젝트는 실제 직원 배정 없이 등급별 인원수만 입력합니다. (MM 집계에 사용)
      </p>
      {/* V69 (2026-05-20): auto-fit + minmax 으로 좁은 화면에서 자연 wrap */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
          gap: '6px',
        }}
      >
        {rows.map((r) => (
          <label key={r.key} style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
            <span style={{ fontSize: '11px', color: '#374151' }}>{r.label}</span>
            <input
              type="number"
              min={0}
              step={0.5}
              value={value[r.key] ?? 0}
              disabled={disabled}
              onChange={(e) => {
                const raw = e.target.value;
                const num = raw === '' ? 0 : Math.max(0, Number(raw));
                onChange({ ...value, [r.key]: Number.isFinite(num) ? num : 0 });
              }}
              style={{
                width: '100%',
                padding: '5px 6px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                textAlign: 'right',
                boxSizing: 'border-box',
                minWidth: 0,
              }}
            />
          </label>
        ))}
      </div>
      <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#374151' }}>
        합계: <strong>{total}</strong> MM
      </p>
    </div>
  );
}

// ============================================
// 생성 모달
// ============================================

interface CreateProjectModalProps {
  scopeOrgUnitIds: string[];
  /** V141: 조직 ID → 조직명 매핑 */
  orgNameMap: Record<string, string>;
  /** V140.3: 스코프 모드 */
  scopeMode?: 'SELF' | 'SUBTREE' | 'ALL';
  /** V97: 고객사 마스터 목록 */
  customerMasters: Customer[];
  onClose: () => void;
  onCreate: (data: CreateProjectRequest) => Promise<void>;
  isLoading: boolean;
  /**
   * V69 (2026-05-20): 과거 프로젝트 모드.
   * - true: 제목 "과거 프로젝트 생성", 조직/직원/리더/외부 인력/staffAssignments 영역 hide.
   * - 실투입은 actualStaffing(등급별 인원수)만 입력. submit 시 staffAssignments=[].
   */
  isHistoricalMode?: boolean;
}

function CreateProjectModal({
  scopeOrgUnitIds,
  orgNameMap,
  scopeMode,
  customerMasters,
  onClose,
  onCreate,
  isLoading,
  isHistoricalMode = false,
}: CreateProjectModalProps) {
  // V97: 고객사 ID → 객체 매핑 (모달 내부용)
  const customerMap = useMemo(() => {
    const map: Record<string, Customer> = {};
    for (const c of customerMasters) {
      map[c.id] = c;
    }
    return map;
  }, [customerMasters]);
  const [name, setName] = useState('');
  const [ownerOrgUnitId, setOwnerOrgUnitId] = useState(scopeOrgUnitIds[0] || '');
  const [description, setDescription] = useState('');

  // V91: 태그 선택
  const [workTypeTags, setWorkTypeTags] = useState<string[]>([]);
  const [projectDomainTags, setProjectDomainTags] = useState<string[]>([]);
  const [workTypeTagMasters, setWorkTypeTagMasters] = useState<ProjectTagMaster[]>([]);
  const [projectDomainTagMasters, setProjectDomainTagMasters] = useState<ProjectTagMaster[]>([]);
  const { listTags } = useProjectTags();

  // V91: 태그 마스터 로드
  useEffect(() => {
    const loadTagMasters = async () => {
      try {
        const [workTypes, domains] = await Promise.all([
          listTags({ type: 'workType' }),
          listTags({ type: 'projectDomain' }),
        ]);
        setWorkTypeTagMasters(workTypes);
        setProjectDomainTagMasters(domains);
      } catch (err) {
        // tag master load failed silently
      }
    };
    loadTagMasters();
  }, [listTags]);

  const [customer, setCustomer] = useState<ProjectCustomer>({ ...EMPTY_CUSTOMER });
  // V97: 고객사 마스터 선택 (드롭다운용)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [contract, setContract] = useState<ProjectContract>({
    ...EMPTY_CONTRACT,
    startDate: new Date().toISOString().slice(0, 10),
  });

  // V85: 적용일/계약일 (프로젝트 생성 시)
  // V116.2 PATCH: effectiveFrom = contract.startDate로 자동 동기화 (SSoT: startDate)
  const [effectiveFrom, setEffectiveFrom] = useState(contract.startDate || new Date().toISOString().slice(0, 10));
  const [contractedAt, setContractedAt] = useState(new Date().toISOString().slice(0, 10));

  // V116.2 PATCH: contract.startDate 변경 시 effectiveFrom 자동 동기화
  React.useEffect(() => {
    if (contract.startDate) {
      setEffectiveFrom(contract.startDate);
    }
  }, [contract.startDate]);

  // V75: 투입 정보 (새 스키마)
  const [actualStaffing, setActualStaffing] = useState<GradeBasedFTE>(createEmptyGradeBasedFTE());
  const [staffAssignments, setStaffAssignments] = useState<StaffAssignment[]>([]);

  // 기존 스키마 (하위호환)
  const [staffingLead, setStaffingLead] = useState<ProjectLead | undefined>(undefined);
  const [staffingMembers, setStaffingMembers] = useState<StaffMember[]>([]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      alert('프로젝트명을 입력하세요.');
      return;
    }
    if (!ownerOrgUnitId) {
      alert('소속 조직을 선택하세요.');
      return;
    }

    // V99.3: 고객사 선택 필수
    if (!selectedCustomerId) {
      alert('고객사를 선택하세요.');
      return;
    }

    // V110: 이메일 형식 검증
    if (customer.contactEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customer.contactEmail)) {
        alert('담당자 이메일 형식이 올바르지 않습니다.\n예: example@company.com');
        return;
      }
    }

    // V69 (2026-05-20) 과거 프로젝트: 실투입(staffAssignments) UI 자체가 없으므로
    // lead/grade-match/FTE-sum 검증을 모두 건너뛰고 staffAssignments=[] 로 저장.
    if (!isHistoricalMode) {
      // V94: 프로젝트 리드 미지정 시 경고 (non-blocking)
      const hasLead = staffAssignments.some((a) => a.role === 'TL');
      if (!hasLead && staffAssignments.length > 0) {
        const proceed = window.confirm('프로젝트 리드가 지정되지 않았습니다.\n그래도 저장하시겠습니까?');
        if (!proceed) return;
      }

      // V122: 투입 MM 합계 검증 (정수 비교로 부동소수점 오류 방지)
      const totalStaffFte = staffAssignments.reduce((sum, a) => sum + a.fte, 0);
      const totalActualStaffingFte = (actualStaffing.special || 0) + (actualStaffing.senior || 0) +
        (actualStaffing.mid || 0) + (actualStaffing.junior || 0) + (actualStaffing.any || 0);

      const staffFteInt = Math.round(totalStaffFte * 2);
      const actualStaffingFteInt = Math.round(totalActualStaffingFte * 2);

      if (staffFteInt !== actualStaffingFteInt) {
        alert(`투입 MM과 투입 인원의 MM이 일치하지 않습니다.\n\n투입인원 등급별 합계: ${totalActualStaffingFte} MM\n직원별 MM 합계: ${totalStaffFte} MM`);
        return;
      }

      // V129.2: 투입인원(actualStaffing) vs 실투입인원(staffAssignments) 등급별 매칭 검증
      const gradeMatchValidation = validateStaffGradeMatchActualStaffing(
        actualStaffing,
        staffAssignments
      );
      if (!gradeMatchValidation.valid) {
        alert(`투입등급과 실투입 등급이 일치하지 않습니다.\n\n${gradeMatchValidation.errors.join('\n')}`);
        return;
      }
    } else {
      // V69 과거 프로젝트: 등급별 인원수 합계 0 인 경우 차단 (MM 집계 불가)
      const totalActualStaffingFte = (actualStaffing.special || 0) + (actualStaffing.senior || 0) +
        (actualStaffing.mid || 0) + (actualStaffing.junior || 0) + (actualStaffing.any || 0);
      if (totalActualStaffingFte <= 0) {
        alert('투입 인원(등급별 인원수)을 1명 이상 입력하세요.');
        return;
      }
    }

    // V76: 레거시 필드(staffing) 제거 - V75 신규 필드만 SSoT
    await onCreate({
      name: name.trim(),
      ownerOrgUnitId,
      description: description.trim() || undefined,
      // V91: 태그 필드 추가
      workTypeTags: workTypeTags.length > 0 ? workTypeTags : undefined,
      projectDomainTags: projectDomainTags.length > 0 ? projectDomainTags : undefined,
      // V98: 고객사 마스터 ID (선택 시)
      customerId: selectedCustomerId || undefined,
      initialRevision: {
        effectiveFrom,  // V85: 적용일
        renewedAt: contractedAt,  // V85: 계약일
        customer,
        contract,
        // V75/V76: 신규 스키마만 저장 (SSoT)
        actualStaffing,
        // V69: 과거 프로젝트는 항상 staffAssignments=[] (부모 handleCreateProject 에서도 한 번 더 강제)
        staffAssignments: isHistoricalMode ? [] : staffAssignments,
        // V76: 레거시 필드(staffing) 더 이상 전송하지 않음
      },
    });
  };

  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClose={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>{isHistoricalMode ? '과거 프로젝트 생성' : '새 프로젝트 생성'}</h2>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'none', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 기본 정보 */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>기본 정보</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label>
              <span style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
                프로젝트명 *
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                }}
              />
            </label>
            {/* V69 (2026-05-20): 과거 프로젝트는 조직명/소속조직을 노출/저장하지 않음.
                ownerOrgUnitId 는 권한 스코프용으로 자동 기본값(scopeOrgUnitIds[0])을 유지. */}
            {!isHistoricalMode && (
              <label>
                <span style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
                  소속 조직 *
                </span>
                <select
                  value={ownerOrgUnitId}
                  onChange={(e) => setOwnerOrgUnitId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                  }}
                >
                  {scopeOrgUnitIds.map((id) => (
                    <option key={id} value={id}>
                      {orgNameMap[id] || id}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              <span style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
                설명
              </span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  resize: 'vertical',
                }}
              />
            </label>
          </div>
        </div>

        {/* V95: 태그 선택 (드롭다운 + 칩 방식) */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>프로젝트 태그</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* 업무형태 */}
            <div>
              <span style={{ display: 'block', fontSize: '13px', marginBottom: '8px', color: '#374151' }}>
                {PROJECT_TAG_TYPE_LABELS.workType}
              </span>
              {/* V95: 드롭다운 (미선택 항목만 표시) */}
              <select
                value=""
                onChange={(e) => {
                  const tagId = e.target.value;
                  if (tagId && !workTypeTags.includes(tagId)) {
                    setWorkTypeTags([...workTypeTags, tagId]);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  fontSize: '13px',
                }}
              >
                <option value="">-- 태그 추가 --</option>
                {workTypeTagMasters
                  .filter((tag) => tag.status === 'active' && !workTypeTags.includes(tag.id))
                  .map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.label}
                    </option>
                  ))}
              </select>
              {/* V95: 선택된 태그 칩 */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minHeight: '28px' }}>
                {workTypeTags.map((tagId) => {
                  const tag = workTypeTagMasters.find((t) => t.id === tagId);
                  return (
                    <span
                      key={tagId}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        borderRadius: '16px',
                        background: '#eef2ff',
                        color: '#4338ca',
                        border: '1px solid #4f46e5',
                      }}
                    >
                      {tag?.label || tagId}
                      <button
                        type="button"
                        onClick={() => setWorkTypeTags(workTypeTags.filter((id) => id !== tagId))}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '0',
                          cursor: 'pointer',
                          color: '#4338ca',
                          fontSize: '14px',
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
                {workTypeTags.length === 0 && (
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>선택된 태그 없음</span>
                )}
              </div>
            </div>
            {/* 프로젝트 분야 */}
            <div>
              <span style={{ display: 'block', fontSize: '13px', marginBottom: '8px', color: '#374151' }}>
                {PROJECT_TAG_TYPE_LABELS.projectDomain}
              </span>
              {/* V95: 드롭다운 (미선택 항목만 표시) */}
              <select
                value=""
                onChange={(e) => {
                  const tagId = e.target.value;
                  if (tagId && !projectDomainTags.includes(tagId)) {
                    setProjectDomainTags([...projectDomainTags, tagId]);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  fontSize: '13px',
                }}
              >
                <option value="">-- 태그 추가 --</option>
                {projectDomainTagMasters
                  .filter((tag) => tag.status === 'active' && !projectDomainTags.includes(tag.id))
                  .map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.label}
                    </option>
                  ))}
              </select>
              {/* V95: 선택된 태그 칩 */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minHeight: '28px' }}>
                {projectDomainTags.map((tagId) => {
                  const tag = projectDomainTagMasters.find((t) => t.id === tagId);
                  return (
                    <span
                      key={tagId}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        borderRadius: '16px',
                        background: '#ecfdf5',
                        color: '#047857',
                        border: '1px solid #059669',
                      }}
                    >
                      {tag?.label || tagId}
                      <button
                        type="button"
                        onClick={() => setProjectDomainTags(projectDomainTags.filter((id) => id !== tagId))}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '0',
                          cursor: 'pointer',
                          color: '#047857',
                          fontSize: '14px',
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
                {projectDomainTags.length === 0 && (
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>선택된 태그 없음</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* V122: 계약 기간 - 계약 일자 위로 이동 */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>계약 기간</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="date"
              value={contract.startDate}
              onChange={(e) => {
                const newStartDate = e.target.value;
                setContract({ ...contract, startDate: newStartDate });
                // V116.2: effectiveFrom 자동 동기화
                setEffectiveFrom(newStartDate);
              }}
              disabled={isLoading}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                flex: 1,
              }}
            />
            <span style={{ color: '#6b7280' }}>~</span>
            <input
              type="date"
              value={contract.endDate}
              onChange={(e) => setContract({ ...contract, endDate: e.target.value })}
              disabled={isLoading}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                flex: 1,
              }}
            />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>
            프로젝트 계약의 시작일과 종료일을 입력하세요
          </p>
        </div>

        {/* V85: 계약 일자 */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>계약 일자</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <label>
              <span style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
                적용일 (계약 효력 발휘일)
              </span>
              {/* V116.2 PATCH: readonly - contract.startDate와 자동 동기화 */}
              <input
                type="date"
                value={effectiveFrom}
                readOnly
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  backgroundColor: '#f1f5f9',
                  color: '#64748b',
                  cursor: 'not-allowed',
                }}
              />
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>
                계약 시작일과 동일하게 자동 설정됩니다
              </p>
            </label>
            <label>
              <span style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
                계약일 (계약 실행일) *
              </span>
              <input
                type="date"
                value={contractedAt}
                onChange={(e) => setContractedAt(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                }}
              />
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>
                계약서 작성/서명일 (과거 날짜 가능)
              </p>
            </label>
          </div>
        </div>

        {/* V99.3: 고객사 정보 - 마스터 선택 필수 + 연락처 입력 */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>고객사 정보</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* V99.3: 고객사 마스터 드롭다운 (필수) */}
            <label>
              <span style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
                고객사 선택 *
              </span>
              <select
                value={selectedCustomerId}
                onChange={(e) => {
                  const customerId = e.target.value;
                  setSelectedCustomerId(customerId);
                  if (customerId) {
                    const selected = customerMap[customerId];
                    if (selected) {
                      // V99.3: 마스터에서 name 스냅샷, 연락처는 유지
                      setCustomer((prev) => ({
                        ...prev,
                        name: selected.name,
                      }));
                    }
                  } else {
                    setCustomer({ ...EMPTY_CUSTOMER });
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  background: 'white',
                }}
              >
                <option value="">-- 고객사를 선택하세요 --</option>
                {/* V99.2: active 고객사만 선택 가능 (archived 제외) */}
                {customerMasters.filter(c => c.status === 'active').map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>
                고객사 마스터에서 선택하세요. 없으면 [고객사] 탭에서 먼저 등록하세요.
              </p>
            </label>
            {/* V99.3: 선택된 고객사명 표시 (읽기 전용) */}
            {selectedCustomerId && (
              <div style={{ background: '#f1f5f9', padding: '8px 12px', borderRadius: '6px' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>고객사명: </span>
                <span style={{ fontWeight: 500 }}>{customer.name}</span>
              </div>
            )}
            {/* V110: 담당자 연락처 (선택) - placeholder/검증 개선 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label>
                <span style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
                  담당자명
                </span>
                <input
                  type="text"
                  value={customer.contactName || ''}
                  onChange={(e) => setCustomer({ ...customer, contactName: e.target.value })}
                  placeholder="담당자명을 입력하세요"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                  }}
                />
              </label>
              <label>
                <span style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
                  연락처
                </span>
                <input
                  type="tel"
                  value={customer.contactPhone || ''}
                  onChange={(e) => {
                    // V110: 숫자와 하이픈만 허용
                    const filtered = e.target.value.replace(/[^0-9-]/g, '');
                    setCustomer({ ...customer, contactPhone: filtered });
                  }}
                  placeholder="연락처를 입력하세요"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                  }}
                />
              </label>
            </div>
            <label>
              <span style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
                담당자 이메일
              </span>
              <input
                type="email"
                value={customer.contactEmail || ''}
                onChange={(e) => setCustomer({ ...customer, contactEmail: e.target.value })}
                placeholder="이메일 주소를 입력하세요"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                }}
              />
            </label>
          </div>
        </div>

        {/* V78: 계약 정보 (계약기간 + 계약인원 + 투입인원) */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>계약 정보</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* 계약기간 + 계약인원 */}
            <ContractEditor
              contract={contract}
              onChange={setContract}
              disabled={isLoading}
            />
            {/* V78: 투입인원 (기존 "투입 정보" 섹션을 계약정보 내부로 이동) */}
            <div>
              <label style={{ fontWeight: 600, fontSize: '13px', color: '#374151', marginBottom: '8px', display: 'block' }}>
                투입 인원
              </label>
              {isHistoricalMode ? (
                /* V69 (2026-05-20): 과거 프로젝트 — 등급별 인원수만 입력 (직원/리더/외부/staffAssignments UI 모두 제거). */
                <HistoricalActualStaffingEditor
                  value={actualStaffing}
                  onChange={setActualStaffing}
                  disabled={isLoading}
                />
              ) : (
                <StaffingEditor
                  // V75: 새 스키마
                  actualStaffing={actualStaffing}
                  staffAssignments={staffAssignments}
                  onChangeActualStaffing={setActualStaffing}
                  onChangeStaffAssignments={setStaffAssignments}
                  // 기존 스키마 (하위호환)
                  lead={staffingLead}
                  members={staffingMembers}
                  onChangeLead={setStaffingLead}
                  onChangeMembers={setStaffingMembers}
                  disabled={isLoading}
                  scopeOrgUnitIds={scopeOrgUnitIds}
                  scopeMode={scopeMode}
                  // V114.4: 프로젝트 투입인원 모드 (KR: KR+CN, CN: CN only)
                  forProjectStaff={true}
                />
              )}
            </div>
          </div>
        </div>

        {/* V78: 단가 정보 (계약정보에서 분리) */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>단가 정보</h3>
          <PricingEditor
            contract={contract}
            onChange={setContract}
            disabled={isLoading}
          />
        </div>

        {/* V78: 메모 (계약정보에서 분리, 최하단 배치) */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>메모</h3>
          <textarea
            value={contract.notes || ''}
            onChange={(e) => setContract({ ...contract, notes: e.target.value })}
            disabled={isLoading}
            placeholder="메모사항을 입력하세요"
            rows={3}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              resize: 'vertical',
            }}
          />
        </div>

        {/* 버튼 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: '8px 16px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
            }}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              background: '#4f46e5',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {isLoading ? '생성 중...' : '생성'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// 프로젝트 상세 Drawer (탭 구조)
// ============================================

type DrawerTab = 'detail' | 'history';

/** V116.6: export for MM pages */
export interface ProjectDetailDrawerProps {
  /** 프로젝트 객체 (OrgProjectsPage에서 사용) */
  project?: ProjectWithRevision | null;
  /** 리비전 이력 (OrgProjectsPage에서 사용) */
  revisions?: ProjectRevision[];
  /** V116.6: 프로젝트 ID (MM 페이지에서 사용 - 자체 로딩) */
  projectId?: string;
  isLoadingDetail?: boolean;
  isMutating?: boolean;
  onClose: () => void;
  onEditSave?: (projectId: string, revisionId: string, patch: Partial<ProjectRevision>) => Promise<any>;
  /** V100: status 파라미터 추가 */
  onRenew?: (projectId: string, newRevision: Omit<ProjectRevision, 'id' | 'projectId' | 'revisionNo' | 'createdAt' | 'createdBy'>, status?: ProjectStatus) => Promise<any>;
  /** V91: 태그 필드 추가 */
  onUpdateMaster?: (projectId: string, patch: Partial<Pick<ProjectWithRevision, 'name' | 'status' | 'description' | 'workTypeTags' | 'projectDomainTags'>>) => Promise<any>;
  /** V140.3: 조직관리 스코프 조직 ID 목록 */
  scopeOrgUnitIds?: string[];
  /** V140.3: 스코프 모드 */
  scopeMode?: 'SELF' | 'SUBTREE' | 'ALL';
  /** V83: 예약 조회 함수 */
  onGetReservations?: (projectId: string, statusFilter?: ProjectRenewalReservationStatus) => Promise<ProjectRenewalReservation[]>;
  /** V83: 예약 취소 함수 */
  onCancelReservation?: (reservationId: string) => Promise<any>;
  /** V99.3: 계약 갱신 예약 함수 */
  onScheduleRenew?: (data: ScheduleProjectRenewalRequest) => Promise<any>;
  /** V99.1: 고객사 마스터 맵 (마스터 우선 표시용) */
  customerMap?: Record<string, Customer>;
  /** V67: 태그 마스터 맵 (예약 상세에서 ID→라벨 변환용) */
  tagMap?: Record<string, ProjectTagMaster>;
  /** V116.6: 읽기 전용 모드 (MM 페이지용) - 편집 UI 숨김 */
  readOnly?: boolean;
}

/** V116.6: export for MM pages */
export function ProjectDetailDrawer({
  project: projectProp,
  revisions: revisionsProp,
  projectId,
  isLoadingDetail: isLoadingDetailProp = false,
  isMutating = false,
  onClose,
  onEditSave,
  onRenew,
  onUpdateMaster,
  scopeOrgUnitIds,
  scopeMode,
  onGetReservations,
  onCancelReservation,
  onScheduleRenew,
  customerMap,
  tagMap,
  readOnly = false,
}: ProjectDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<DrawerTab>('detail');
  const [selectedHistoryRevision, setSelectedHistoryRevision] = useState<ProjectRevision | null>(null);

  // V67: tagMap 이 prop 으로 안 들어오면 자체 로드 (예: MM readOnly drawer)
  const [selfLoadedTagMap, setSelfLoadedTagMap] = useState<Record<string, ProjectTagMaster>>({});
  const { listTags: selfListTags } = useProjectTags();
  useEffect(() => {
    if (tagMap) return;
    (async () => {
      try {
        const tags = await selfListTags();
        const m: Record<string, ProjectTagMaster> = {};
        for (const t of tags) m[t.id] = t;
        setSelfLoadedTagMap(m);
      } catch (e) {
        console.warn('[ProjectDetailDrawer] tagMap self-load failed', e);
      }
    })();
  }, [tagMap, selfListTags]);
  const effectiveTagMap = tagMap ?? selfLoadedTagMap;

  // V116.6: Self-loading mode 상태 (projectId로 열릴 때)
  const [selfLoadedProject, setSelfLoadedProject] = useState<ProjectWithRevision | null>(null);
  const [selfLoadedRevisions, setSelfLoadedRevisions] = useState<ProjectRevision[]>([]);
  const [isSelfLoading, setIsSelfLoading] = useState(false);
  const [selfLoadError, setSelfLoadError] = useState<string | null>(null);

  // V116.6: projectId 모드일 때 데이터 로딩
  useEffect(() => {
    if (!projectId || projectProp) return; // projectProp이 있으면 self-loading 불필요

    const loadProject = async () => {
      setIsSelfLoading(true);
      setSelfLoadError(null);
      try {
        const getProjectHistoryFn = httpsCallable<GetProjectHistoryRequest, GetProjectHistoryResponse>(
          functions!,
          'getProjectHistory'
        );
        const response = await getProjectHistoryFn({ projectId });
        const data = response.data;

        if (data.success && data.project) {
          // V116.6: getProjectHistory는 Project를 반환하지만 currentRevision을 채워야 함
          const revs = data.revisions || [];
          const currentRevision = revs.find(r => r.id === data.project!.currentRevisionId);
          const projectWithRevision: ProjectWithRevision = {
            ...data.project,
            currentRevision,
          };
          setSelfLoadedProject(projectWithRevision);
          setSelfLoadedRevisions(revs);
        } else {
          setSelfLoadError(data.error || '프로젝트를 불러올 수 없습니다.');
        }
      } catch (err: any) {
        console.error('[ProjectDetailDrawer] 프로젝트 로드 실패:', err);
        setSelfLoadError(err.message || '프로젝트를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsSelfLoading(false);
      }
    };

    loadProject();
  }, [projectId, projectProp]);

  // V116.6: 실제 사용할 project/revisions 결정
  const project = projectProp || selfLoadedProject;
  const revisions = revisionsProp || selfLoadedRevisions;
  const isLoadingDetail = isLoadingDetailProp || isSelfLoading;

  // V116.6: 로딩 중이거나 프로젝트가 없으면 로딩/에러 UI 표시
  if (!project) {
    return (
      <>
        {/* Backdrop */}
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 998,
          }}
          onClick={onClose}
        />
        {/* Drawer */}
        <div
          style={{
            position: 'fixed',
            right: 0,
            top: 0,
            bottom: 0,
            width: '560px',
            background: 'white',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
              {isSelfLoading ? '로딩 중...' : '프로젝트 상세'}
            </h2>
            <button
              onClick={onClose}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
              }}
            >
              <X size={20} />
            </button>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isSelfLoading ? (
              <span style={{ color: '#94a3b8' }}>로딩 중...</span>
            ) : selfLoadError ? (
              <span style={{ color: '#ef4444' }}>{selfLoadError}</span>
            ) : (
              <span style={{ color: '#94a3b8' }}>프로젝트를 불러올 수 없습니다.</span>
            )}
          </div>
        </div>
      </>
    );
  }

  const currentRevision = project.currentRevision;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 998,
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: '560px',
          background: 'white',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>{project.name}</h2>
            <span
              style={{
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500,
                background: getStatusColor(project.status).bg,
                color: getStatusColor(project.status).text,
              }}
            >
              {PROJECT_STATUS_LABELS[project.status]}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 탭 바 */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
            background: '#f8fafc',
          }}
        >
          <button
            onClick={() => {
              setActiveTab('detail');
              setSelectedHistoryRevision(null);
            }}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              background: activeTab === 'detail' ? 'white' : 'transparent',
              borderBottom: activeTab === 'detail' ? '2px solid #4f46e5' : '2px solid transparent',
              color: activeTab === 'detail' ? '#4f46e5' : '#64748b',
              fontWeight: activeTab === 'detail' ? 600 : 400,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <FileText size={16} />
            상세
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              background: activeTab === 'history' ? 'white' : 'transparent',
              borderBottom: activeTab === 'history' ? '2px solid #4f46e5' : '2px solid transparent',
              color: activeTab === 'history' ? '#4f46e5' : '#64748b',
              fontWeight: activeTab === 'history' ? 600 : 400,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <History size={16} />
            이력 ({revisions.length})
          </button>
        </div>

        {/* 탭 컨텐츠 */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {isLoadingDetail ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
              로딩 중...
            </div>
          ) : activeTab === 'detail' ? (
            <DetailTab
              project={project}
              revision={currentRevision}
              isMutating={isMutating}
              onEditSave={onEditSave}
              onRenew={onRenew}
              onUpdateMaster={onUpdateMaster}
              scopeOrgUnitIds={scopeOrgUnitIds}
              scopeMode={scopeMode}
              onGetReservations={onGetReservations}
              onCancelReservation={onCancelReservation}
              onScheduleRenew={onScheduleRenew}
              customerMap={customerMap}
              readOnly={readOnly}
            />
          ) : (
            <HistoryTab
              project={project}
              revisions={revisions}
              selectedRevision={selectedHistoryRevision}
              onSelectRevision={setSelectedHistoryRevision}
              customerMap={customerMap}
              tagMap={effectiveTagMap}
              onGetReservations={onGetReservations}
              onCancelReservation={onCancelReservation}
              readOnly={readOnly}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ============================================
// 상세 탭
// ============================================

interface DetailTabProps {
  project: ProjectWithRevision;
  revision: ProjectRevision | undefined;
  isMutating?: boolean;
  /** V116.6: readOnly 모드에서는 optional */
  onEditSave?: (projectId: string, revisionId: string, patch: Partial<ProjectRevision>) => Promise<any>;
  /** V100: status 파라미터 추가; V116.6: readOnly 모드에서는 optional */
  onRenew?: (projectId: string, newRevision: Omit<ProjectRevision, 'id' | 'projectId' | 'revisionNo' | 'createdAt' | 'createdBy'>, status?: ProjectStatus) => Promise<any>;
  /** V91: 태그 필드 추가; V116.6: readOnly 모드에서는 optional */
  onUpdateMaster?: (projectId: string, patch: Partial<Pick<ProjectWithRevision, 'name' | 'status' | 'description' | 'workTypeTags' | 'projectDomainTags'>>) => Promise<any>;
  /** V140.3: 조직관리 스코프 조직 ID 목록 */
  scopeOrgUnitIds?: string[];
  /** V140.3: 스코프 모드 */
  scopeMode?: 'SELF' | 'SUBTREE' | 'ALL';
  /** V83: 예약 조회 함수 */
  onGetReservations?: (projectId: string, statusFilter?: ProjectRenewalReservationStatus) => Promise<ProjectRenewalReservation[]>;
  /** V83: 예약 취소 함수 */
  onCancelReservation?: (reservationId: string) => Promise<any>;
  /** V99.3: 계약 갱신 예약 함수 */
  onScheduleRenew?: (data: ScheduleProjectRenewalRequest) => Promise<any>;
  /** V99.4: 고객사 마스터 맵 (마스터 우선 표시용) */
  customerMap?: Record<string, Customer>;
  /** V116.6: 읽기 전용 모드 */
  readOnly?: boolean;
}

function DetailTab({
  project,
  revision,
  isMutating,
  onEditSave,
  onRenew,
  onUpdateMaster,
  scopeOrgUnitIds,
  scopeMode,
  onGetReservations,
  onCancelReservation,
  onScheduleRenew,
  customerMap,
  readOnly = false,
}: DetailTabProps) {
  // V82: 편집 모드 선택 팝업
  const [showEditModeDialog, setShowEditModeDialog] = useState(false);
  // V82: 현재 편집 모드 (null = 편집 안함)
  const [editMode, setEditMode] = useState<ProjectEditMode | null>(null);
  // V82: 기존 isEditing은 editMode !== null 로 대체
  const isEditing = editMode !== null;
  // V82: 기존 showRenewDialog 제거 (편집 모드 선택으로 통합)

  // 마스터 편집 상태
  const [editName, setEditName] = useState(project.name);
  const [editStatus, setEditStatus] = useState(project.status);
  const [editDescription, setEditDescription] = useState(project.description || '');

  // 리비전 편집 상태
  const [editCustomer, setEditCustomer] = useState<ProjectCustomer>(
    revision?.customer || { ...EMPTY_CUSTOMER }
  );
  const [editContract, setEditContract] = useState<ProjectContract>(
    revision?.contract || { ...EMPTY_CONTRACT }
  );
  // V75: 투입 정보 편집 상태 (새 스키마)
  const [editActualStaffing, setEditActualStaffing] = useState<GradeBasedFTE>(
    revision?.actualStaffing || createEmptyGradeBasedFTE()
  );
  const [editStaffAssignments, setEditStaffAssignments] = useState<StaffAssignment[]>(
    revision?.staffAssignments || []
  );

  // 기존 스키마 (하위호환)
  const [editStaffingLead, setEditStaffingLead] = useState<ProjectLead | undefined>(
    revision?.staffing?.lead
  );
  const [editStaffingMembers, setEditStaffingMembers] = useState<StaffMember[]>(
    revision?.staffing?.members || []
  );

  // 갱신용 상태
  const [renewEffectiveFrom, setRenewEffectiveFrom] = useState(
    new Date().toISOString().slice(0, 10)
  );
  // V82: 계약일 (계약 갱신 시 필수)
  const [renewedAt, setRenewedAt] = useState(
    new Date().toISOString().slice(0, 10)
  );
  // V82: 예약일 (계약 갱신 예약 시)
  const [scheduledAt, setScheduledAt] = useState('');

  // V121: 덮어쓰기 편집용 상태 (effectiveFrom, renewedAt)
  const [editEffectiveFrom, setEditEffectiveFrom] = useState(
    revision?.effectiveFrom || new Date().toISOString().slice(0, 10)
  );
  const [editRenewedAt, setEditRenewedAt] = useState(
    revision?.renewedAt || ''
  );

  // V91: 태그 편집 상태
  const [editWorkTypeTags, setEditWorkTypeTags] = useState<string[]>(project.workTypeTags || []);
  const [editProjectDomainTags, setEditProjectDomainTags] = useState<string[]>(project.projectDomainTags || []);
  const [workTypeTagMasters, setWorkTypeTagMasters] = useState<ProjectTagMaster[]>([]);
  const [projectDomainTagMasters, setProjectDomainTagMasters] = useState<ProjectTagMaster[]>([]);
  const { listTags } = useProjectTags();

  // V91.1: 태그 마스터 로드 (active + archived 모두 로드하여 이미 선택된 archived 태그 표시)
  useEffect(() => {
    const loadTagMasters = async () => {
      try {
        // V91.1: includeArchived=true로 모든 태그 로드 (이미 선택된 archived 태그 표시용)
        const [workTypes, domains] = await Promise.all([
          listTags({ type: 'workType', includeArchived: true }),
          listTags({ type: 'projectDomain', includeArchived: true }),
        ]);
        setWorkTypeTagMasters(workTypes);
        setProjectDomainTagMasters(domains);
      } catch (err) {
        // V91.1: 권한 부족 시 active만 로드 시도
        // tag master full load failed, try active only
        try {
          const [workTypes, domains] = await Promise.all([
            listTags({ type: 'workType' }),
            listTags({ type: 'projectDomain' }),
          ]);
          setWorkTypeTagMasters(workTypes);
          setProjectDomainTagMasters(domains);
        } catch (err2) {
          // tag master fallback load failed silently
        }
      }
    };
    loadTagMasters();
  }, [listTags]);

  // V83: pending 예약 상태
  const [pendingReservation, setPendingReservation] = useState<ProjectRenewalReservation | null>(null);
  const [isCancellingReservation, setIsCancellingReservation] = useState(false);

  // V83: pending 예약 조회 effect
  useEffect(() => {
    const fetchPendingReservation = async () => {
      if (!onGetReservations || !project.id) {
        setPendingReservation(null);
        return;
      }
      try {
        const reservations = await onGetReservations(project.id, 'pending');
        setPendingReservation(reservations.length > 0 ? reservations[0] : null);
      } catch (err) {
        console.error('[DetailTab] pending 예약 조회 실패:', err);
        setPendingReservation(null);
      }
    };
    fetchPendingReservation();
  }, [project.id, onGetReservations]);

  // V83: 예약 취소 핸들러
  const handleCancelReservation = async () => {
    if (!pendingReservation || !onCancelReservation) return;
    if (!window.confirm(`예약된 갱신을 취소하시겠습니까?\n예약일: ${pendingReservation.scheduledAt}`)) return;

    setIsCancellingReservation(true);
    try {
      const result = await onCancelReservation(pendingReservation.id);
      if (result.success) {
        alert('예약이 취소되었습니다.');
        setPendingReservation(null);
      } else {
        alert(`취소 실패: ${result.error || '알 수 없는 오류'}`);
      }
    } catch (err: any) {
      alert(`취소 오류: ${err.message}`);
    } finally {
      setIsCancellingReservation(false);
    }
  };

  // project/revision 변경 시 편집 상태 초기화
  useEffect(() => {
    setEditName(project.name);
    setEditStatus(project.status);
    setEditDescription(project.description || '');
    setEditCustomer(revision?.customer || { ...EMPTY_CUSTOMER });
    setEditContract(revision?.contract || { ...EMPTY_CONTRACT });
    // V75: 투입 정보 초기화 (새 스키마)
    setEditActualStaffing(revision?.actualStaffing || createEmptyGradeBasedFTE());
    setEditStaffAssignments(revision?.staffAssignments || []);
    // 기존 스키마 (하위호환)
    setEditStaffingLead(revision?.staffing?.lead);
    setEditStaffingMembers(revision?.staffing?.members || []);
    // V91: 태그 초기화
    setEditWorkTypeTags(project.workTypeTags || []);
    setEditProjectDomainTags(project.projectDomainTags || []);
    // V82: 편집 모드 초기화
    setEditMode(null);
    setShowEditModeDialog(false);
  }, [project, revision]);

  // V82: 편집 모드 선택 핸들러
  const handleSelectEditMode = (mode: ProjectEditMode) => {
    setShowEditModeDialog(false);
    setEditMode(mode);
    // 날짜 초기화
    const today = new Date().toISOString().slice(0, 10);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().slice(0, 10);
    // V67: scheduleRenew 는 effectiveFrom 도 내일 기본값 (오늘 금지 정책)
    setRenewEffectiveFrom(mode === 'scheduleRenew' ? tomorrowStr : today);
    setRenewedAt(today);
    setScheduledAt(tomorrowStr);
    // V121: 덮어쓰기 편집용 상태 초기화 (기존 리비전 값으로)
    setEditEffectiveFrom(revision?.effectiveFrom || today);
    setEditRenewedAt(revision?.renewedAt || '');
  };

  // V82: 취소 핸들러 (편집 종료)
  const handleCancelEdit = () => {
    setEditMode(null);
    // 편집 상태 초기화
    setEditName(project.name);
    setEditStatus(project.status);
    setEditDescription(project.description || '');
    setEditCustomer(revision?.customer || { ...EMPTY_CUSTOMER });
    setEditContract(revision?.contract || { ...EMPTY_CONTRACT });
    setEditActualStaffing(revision?.actualStaffing || createEmptyGradeBasedFTE());
    setEditStaffAssignments(revision?.staffAssignments || []);
    // V91: 태그 초기화
    setEditWorkTypeTags(project.workTypeTags || []);
    setEditProjectDomainTags(project.projectDomainTags || []);
    // V121: 날짜 필드 초기화
    setEditEffectiveFrom(revision?.effectiveFrom || new Date().toISOString().slice(0, 10));
    setEditRenewedAt(revision?.renewedAt || '');
  };

  /**
   * V82: 저장 핸들러 (모드에 따라 다른 동작)
   * V91.2: 저장 흐름 개선
   * - 단계별 에러 추적
   * - 상세 에러 메시지 표시
   * - 부분 성공 시 상태 표시
   */
  const handleSave = async () => {
    if (!revision || !editMode) return;

    // V69 (2026-05-20): historical 가드 — CREATE handleSave (line 1985-2023) 의 isHistoricalMode 분기와 동일 정책.
    //   historical 편집은 staffAssignments=[] 가 정상 상태라 staffAssignments 기준 검증 (V94/V122/V129.2) 모두 우회.
    //   대신 actualStaffing 합계가 0 이하이면 차단 (CREATE 의 line 2019-2022 와 동일 가드).
    const isHistoricalProject = (project as any).isHistorical === true;

    if (!isHistoricalProject) {
      // V94: 프로젝트 리드 미지정 시 경고 (non-blocking)
      const hasLead = editStaffAssignments.some((a) => a.role === 'TL');
      if (!hasLead && editStaffAssignments.length > 0) {
        const modeLabel = editMode === 'overwrite' ? '저장' : (editMode === 'renew' ? '갱신' : '예약');
        const proceed = window.confirm(`프로젝트 리드가 지정되지 않았습니다.\n그래도 ${modeLabel}하시겠습니까?`);
        if (!proceed) return;
      }

      // V122: 투입 MM 합계 검증 (정수 비교로 부동소수점 오류 방지)
      const totalStaffFte = editStaffAssignments.reduce((sum, a) => sum + a.fte, 0);
      const totalActualStaffingFte = (editActualStaffing?.special || 0) + (editActualStaffing?.senior || 0) +
        (editActualStaffing?.mid || 0) + (editActualStaffing?.junior || 0) + (editActualStaffing?.any || 0);

      // 정수 비교 (*2하여 0.5 단위를 1 단위로 변환)
      const staffFteInt = Math.round(totalStaffFte * 2);
      const actualStaffingFteInt = Math.round(totalActualStaffingFte * 2);

      if (staffFteInt !== actualStaffingFteInt) {
        alert(`투입 MM과 투입 인원의 MM이 일치하지 않습니다.\n\n투입인원 등급별 합계: ${totalActualStaffingFte} MM\n직원별 MM 합계: ${totalStaffFte} MM`);
        return;
      }

      // V129.2: 투입인원(actualStaffing) vs 실투입인원(staffAssignments) 등급별 매칭 검증
      const gradeMatchValidation = validateStaffGradeMatchActualStaffing(
        editActualStaffing,
        editStaffAssignments
      );
      if (!gradeMatchValidation.valid) {
        alert(`투입등급과 실투입 등급이 일치하지 않습니다.\n\n${gradeMatchValidation.errors.join('\n')}`);
        return;
      }
    } else {
      // V69 (2026-05-20): 과거 프로젝트 — 등급별 인원수 합계 0 인 경우 차단 (MM 집계 불가). CREATE flow line 2019-2022 와 동일.
      const totalActualStaffingFte = (editActualStaffing?.special || 0) + (editActualStaffing?.senior || 0) +
        (editActualStaffing?.mid || 0) + (editActualStaffing?.junior || 0) + (editActualStaffing?.any || 0);
      if (totalActualStaffingFte <= 0) {
        alert('투입 인원(등급별 인원수)을 1명 이상 입력하세요.');
        return;
      }
    }

    // V110: 이메일 형식 검증
    if (editCustomer.contactEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editCustomer.contactEmail)) {
        alert('담당자 이메일 형식이 올바르지 않습니다.\n예: example@company.com');
        return;
      }
    }

    // V82: 갱신/예약 갱신 시 계약일 필수 체크
    if ((editMode === 'renew' || editMode === 'scheduleRenew') && !renewedAt) {
      alert('계약일을 입력해주세요.');
      return;
    }

    // V82: 예약 갱신 시 적용일 필수 체크 (예약 적용일은 적용일과 자동 동기화되므로 적용일만 체크)
    // V68: 메시지를 사용자가 실제로 입력하는 항목명("적용일")으로 통일.
    if (editMode === 'scheduleRenew' && !scheduledAt) {
      alert('적용일을 입력해주세요.');
      return;
    }

    // V82: 예약 갱신 시 예약일이 오늘 이후인지 체크 (scheduledAt === renewEffectiveFrom 보장)
    if (editMode === 'scheduleRenew') {
      const today = new Date().toISOString().slice(0, 10);
      if (scheduledAt <= today) {
        alert('적용일은 내일 이후 날짜를 선택해주세요.\n(새벽 00:05 KST에 자동 적용됩니다)');
        return;
      }
    }

    // V91.2: 저장 결과 추적
    const saveResults: { step: string; success: boolean; error?: string }[] = [];

    try {
      // V91.1: 태그 변경 여부 체크 (모든 모드에서 공통)
      const tagsChanged =
        JSON.stringify([...editWorkTypeTags].sort()) !== JSON.stringify([...(project.workTypeTags || [])].sort()) ||
        JSON.stringify([...editProjectDomainTags].sort()) !== JSON.stringify([...(project.projectDomainTags || [])].sort());

      // V91.2: 마스터 변경 확인 (태그 외)
      const masterChanged =
        editName !== project.name ||
        editStatus !== project.status ||
        editDescription !== (project.description || '');

      // V109: Step 1 - 마스터 정보 업데이트
      // - scheduleRenew 모드: 마스터 업데이트 하지 않음 (예약 시 즉시 반영 방지)
      // - renew 모드: 태그 + 이름/설명 변경 시 적용 (status는 onRenew로 별도 처리)
      // - overwrite 모드: 태그 + 기본 정보 모두 즉시 적용
      // V55.36: renew 모드에서 name/description 변경 시에도 마스터 업데이트 필요
      const nameOrDescChanged =
        editName !== project.name ||
        editDescription !== (project.description || '');

      const needsMasterUpdate =
        editMode !== 'scheduleRenew' &&
        (tagsChanged ||
         (editMode === 'overwrite' && masterChanged) ||
         (editMode === 'renew' && nameOrDescChanged));

      if (needsMasterUpdate) {
        try {
          const masterUpdateData: Record<string, unknown> = {};

          // 태그 변경
          if (tagsChanged) {
            masterUpdateData.workTypeTags = editWorkTypeTags.length > 0 ? editWorkTypeTags : [];
            masterUpdateData.projectDomainTags = editProjectDomainTags.length > 0 ? editProjectDomainTags : [];
          }

          // 기본 정보 변경 (overwrite 또는 renew 모드)
          // V55.36: renew 모드에서도 name/description 업데이트 지원
          if (editMode === 'overwrite' && masterChanged) {
            masterUpdateData.name = editName;
            masterUpdateData.status = editStatus;
            masterUpdateData.description = editDescription || undefined;
          } else if (editMode === 'renew' && nameOrDescChanged) {
            // renew 모드: name/description만 (status는 onRenew에서 별도 처리)
            masterUpdateData.name = editName;
            masterUpdateData.description = editDescription || undefined;
          }

          await onUpdateMaster?.(project.id, masterUpdateData);
          saveResults.push({ step: '프로젝트 마스터 정보', success: true });
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : '알 수 없는 오류';
          saveResults.push({ step: '프로젝트 마스터 정보', success: false, error: errorMsg });
          console.error('[V91.2] Step 1 실패:', err);
        }
      }

      // V91.2: Step 2 - 모드별 리비전 처리
      // V69 (2026-05-20): isHistoricalProject 는 함수 진입부에서 이미 hoist 됨 (line 3234) — 재선언 제거
      try {
        if (editMode === 'overwrite') {
          await onEditSave?.(project.id, revision.id, {
            effectiveFrom: editEffectiveFrom,
            renewedAt: editRenewedAt || undefined,  // 빈 문자열이면 undefined로
            customer: editCustomer,
            contract: editContract,
            actualStaffing: editActualStaffing,
            // V69 (2026-05-20): 과거 프로젝트는 항상 staffAssignments=[] (BE guard 와 동일)
            staffAssignments: isHistoricalProject ? [] : editStaffAssignments,
          });
          saveResults.push({ step: '리비전 저장', success: true });
        } else if (editMode === 'renew') {
          // V60: 적용일 역전/동일 차단
          if (revision?.effectiveFrom && renewEffectiveFrom <= revision.effectiveFrom) {
            throw new Error(`새 리비전 적용일은 현재 버전 적용일(${revision.effectiveFrom})보다 이후여야 합니다.`);
          }
          // V100: status 변경이 있으면 함께 전달
          const statusChanged = editStatus !== project.status;
          await onRenew?.(
            project.id,
            {
              effectiveFrom: renewEffectiveFrom,
              renewedAt: renewedAt,
              customer: editCustomer,
              contract: editContract,
              actualStaffing: editActualStaffing,
              staffAssignments: editStaffAssignments,
            },
            statusChanged ? editStatus : undefined
          );
          saveResults.push({ step: '계약 갱신', success: true });
        } else if (editMode === 'scheduleRenew') {
          // V60: 적용일 역전/동일 차단
          if (revision?.effectiveFrom && renewEffectiveFrom <= revision.effectiveFrom) {
            throw new Error(`새 리비전 적용일은 현재 버전 적용일(${revision.effectiveFrom})보다 이후여야 합니다.`);
          }
          // V100: 계약 갱신 예약 - status 변경 포함
          if (!onScheduleRenew) {
            throw new Error('계약 갱신 예약 기능이 연결되지 않았습니다.');
          }
          if (!scheduledAt) {
            throw new Error('예약 일시가 설정되지 않았습니다.');
          }
          const statusChanged = editStatus !== project.status;
          // V275: 마스터 정보 변경 여부 확인 (name/description/tags)
          const masterUpdate: Record<string, unknown> = {};
          if (editName !== project.name) masterUpdate.name = editName;
          if (editDescription !== (project.description || '')) masterUpdate.description = editDescription || '';
          if (tagsChanged) {
            masterUpdate.workTypeTags = editWorkTypeTags.length > 0 ? editWorkTypeTags : [];
            masterUpdate.projectDomainTags = editProjectDomainTags.length > 0 ? editProjectDomainTags : [];
          }
          const hasMasterUpdate = Object.keys(masterUpdate).length > 0;

          await onScheduleRenew({
            projectId: project.id,
            scheduledAt: scheduledAt,
            newRevision: {
              effectiveFrom: renewEffectiveFrom,
              renewedAt: renewedAt,
              customer: editCustomer,
              contract: editContract,
              actualStaffing: editActualStaffing,
              staffAssignments: editStaffAssignments,
            },
            // V100: status 변경이 있으면 함께 전달
            ...(statusChanged && { status: editStatus }),
            // V275: 마스터 정보 변경이 있으면 함께 전달
            ...(hasMasterUpdate && { masterUpdate }),
          });
          saveResults.push({ step: '계약 갱신 예약', success: true });
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : '알 수 없는 오류';
        const stepName = editMode === 'overwrite' ? '리비전 저장' : (editMode === 'renew' ? '계약 갱신' : '계약 갱신 예약');
        saveResults.push({ step: stepName, success: false, error: errorMsg });
        console.error('[V91.2] Step 2 실패:', err);
      }

      // V91.2: 결과 분석 및 표시
      const failedSteps = saveResults.filter(r => !r.success);
      const successSteps = saveResults.filter(r => r.success);

      if (failedSteps.length > 0) {
        const failedMessages = failedSteps.map(r => `• ${r.step}: ${r.error}`).join('\n');
        const successMessages = successSteps.length > 0
          ? `\n\n✅ 성공:\n${successSteps.map(r => `• ${r.step}`).join('\n')}`
          : '';

        alert(`⚠️ 저장 중 일부 오류가 발생했습니다.\n\n❌ 실패:\n${failedMessages}${successMessages}`);

        // 부분 성공 시에도 editMode 해제 (사용자가 상태 확인 후 재시도 가능)
        if (successSteps.length > 0) {
          setEditMode(null);
        }
      } else {
        // 모든 단계 성공
        setEditMode(null);
      }
    } catch (err) {
      // V91.2: 예상치 못한 오류
      console.error('[V91.2] handleSave 예상치 못한 오류:', err);
      alert(`저장 중 오류가 발생했습니다.\n\n${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    background: isEditing ? 'white' : '#f8fafc',
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* V69 (2026-05-21): WRITE 권한 없음 → 조회 전용 안내 배너.
          본인 BE-side scope 외 프로젝트는 read 가능하나 편집/갱신/예약/삭제 UI 모두 비활성. */}
      {readOnly && (
        <div
          style={{
            padding: '10px 14px',
            marginBottom: '16px',
            borderRadius: '8px',
            background: '#eff6ff',
            border: '1px solid #93c5fd',
            color: '#1e40af',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <AlertCircle size={16} />
          <span>조회 전용 프로젝트입니다. (본인 권한 범위 외 — 편집/갱신/예약/삭제는 불가)</span>
        </div>
      )}
      {/* V82: 편집 모드 선택 팝업 */}
      {showEditModeDialog && (
        <ModalOverlay
          onClose={() => setShowEditModeDialog(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '400px',
              maxWidth: '500px',
            }}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>
              편집 방식 선택
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#6b7280' }}>
              편집 방식을 선택해주세요. 선택 후에는 변경할 수 없습니다.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* 현재 계약 편집 */}
              <button
                onClick={() => handleSelectEditMode('overwrite')}
                style={{
                  padding: '16px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <Save size={16} style={{ color: '#3b82f6' }} />
                  <span style={{ fontWeight: 600, fontSize: '14px' }}>현재 계약 편집</span>
                </div>
                <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                  {PROJECT_EDIT_MODE_DESCRIPTIONS.overwrite}
                </p>
              </button>
              {/* V69 (2026-05-20): 과거 프로젝트는 계약 갱신/예약 갱신 불가 (실투입 직원 매핑 없음 + BE guard) */}
              {!(project as any).isHistorical && (
                <button
                  onClick={() => handleSelectEditMode('renew')}
                  style={{
                    padding: '16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <RotateCw size={16} style={{ color: '#059669' }} />
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>계약 갱신</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                    {PROJECT_EDIT_MODE_DESCRIPTIONS.renew}
                  </p>
                </button>
              )}
              {/* V69 (2026-05-20): 과거 프로젝트는 계약 갱신 예약 불가 */}
              {(project as any).isHistorical ? null : pendingReservation ? (
                // V83: pending 예약이 있는 경우
                <div
                  style={{
                    padding: '16px',
                    border: '1px solid #fcd34d',
                    borderRadius: '8px',
                    background: '#fffbeb',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <AlertCircle size={16} style={{ color: '#f59e0b' }} />
                    <span style={{ fontWeight: 600, fontSize: '14px', color: '#92400e' }}>
                      이미 예약된 갱신이 있습니다
                    </span>
                  </div>
                  <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#92400e' }}>
                    예약 적용일: {pendingReservation.scheduledAt} (00:05 KST 자동 적용)
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelReservation();
                    }}
                    disabled={isCancellingReservation}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #dc2626',
                      borderRadius: '4px',
                      background: 'white',
                      color: '#dc2626',
                      cursor: isCancellingReservation ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      opacity: isCancellingReservation ? 0.5 : 1,
                    }}
                  >
                    {isCancellingReservation ? '취소 중...' : '예약 취소'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleSelectEditMode('scheduleRenew')}
                  style={{
                    padding: '16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Clock size={16} style={{ color: '#f59e0b' }} />
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>계약 갱신 예약</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                    {PROJECT_EDIT_MODE_DESCRIPTIONS.scheduleRenew}
                  </p>
                </button>
              )}
            </div>
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={() => setShowEditModeDialog(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                취소
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* V82: 현재 편집 모드 배지 */}
      {isEditing && editMode && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: '16px',
            borderRadius: '8px',
            background: editMode === 'overwrite' ? '#eff6ff' : (editMode === 'renew' ? '#ecfdf5' : '#fffbeb'),
            border: `1px solid ${editMode === 'overwrite' ? '#93c5fd' : (editMode === 'renew' ? '#6ee7b7' : '#fcd34d')}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {editMode === 'overwrite' && <Save size={16} style={{ color: '#3b82f6' }} />}
            {editMode === 'renew' && <RotateCw size={16} style={{ color: '#059669' }} />}
            {editMode === 'scheduleRenew' && <Clock size={16} style={{ color: '#f59e0b' }} />}
            <span style={{ fontWeight: 600, fontSize: '13px' }}>
              편집 모드: {PROJECT_EDIT_MODE_LABELS[editMode]}
            </span>
            {editMode === 'scheduleRenew' && scheduledAt && (
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                ({scheduledAt} 새벽 적용 예정)
              </span>
            )}
          </div>
          <button
            onClick={handleCancelEdit}
            style={{
              padding: '4px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <X size={12} />
            취소
          </button>
        </div>
      )}

      {/* V123: 기본 정보 섹션 (정본: CreateProjectModal과 동일한 라벨) */}
      <section style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151' }}>
            기본 정보
          </h3>
          {/* V116.6: readOnly 모드에서는 편집 버튼 숨김 */}
          {!isEditing && !readOnly && (
            <button
              onClick={() => setShowEditModeDialog(true)}
              disabled={isMutating}
              style={{
                padding: '4px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Edit3 size={12} />
              편집
            </button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
              프로젝트명
            </label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              disabled={!isEditing}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
              상태
            </label>
            <select
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as ProjectStatus)}
              disabled={!isEditing}
              style={inputStyle}
            >
              <option value="active">진행중</option>
              <option value="completed">완료</option>
              <option value="onHold">중단</option>
              <option value="cancelled">취소</option>
            </select>
          </div>
          {/* V69 (2026-05-20): 프로젝트 이력(isHistorical=true) 은 조직 개념 없음 — 입력 필드 자체를 hide.
              일반 프로젝트는 기존대로 ownerOrgUnitNameSnapshot 표시. */}
          {(project as any).isHistorical !== true && (
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                소속 조직
              </label>
              <input
                type="text"
                value={project.ownerOrgUnitNameSnapshot}
                disabled
                style={{ ...inputStyle, background: '#f1f5f9', color: '#64748b' }}
              />
            </div>
          )}
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
              설명
            </label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              disabled={!isEditing}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        </div>
      </section>

      {/* V123: 프로젝트 태그 섹션 (정본: CreateProjectModal과 동일하게 별도 섹션) */}
      <section style={{ marginBottom: '24px' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
          프로젝트 태그
        </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* 업무형태 */}
            <div>
              <span style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>
                {PROJECT_TAG_TYPE_LABELS.workType}
              </span>
              {isEditing ? (
                <div>
                  {/* V96: 드롭다운 (미선택 + active 항목만 표시) */}
                  <select
                    value=""
                    onChange={(e) => {
                      const tagId = e.target.value;
                      if (tagId && !editWorkTypeTags.includes(tagId)) {
                        setEditWorkTypeTags([...editWorkTypeTags, tagId]);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '4px 8px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '4px',
                      marginBottom: '6px',
                      fontSize: '11px',
                    }}
                  >
                    <option value="">-- 태그 추가 --</option>
                    {workTypeTagMasters
                      .filter((tag) => tag.status === 'active' && !editWorkTypeTags.includes(tag.id))
                      .map((tag) => (
                        <option key={tag.id} value={tag.id}>
                          {tag.label}
                        </option>
                      ))}
                  </select>
                  {/* V96: 선택된 태그 칩 */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', minHeight: '20px' }}>
                    {editWorkTypeTags.map((tagId) => {
                      const tag = workTypeTagMasters.find((t) => t.id === tagId);
                      const isArchived = tag?.status === 'archived';
                      return (
                        <span
                          key={tagId}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            padding: '2px 6px',
                            fontSize: '11px',
                            borderRadius: '12px',
                            background: isArchived ? '#f3f4f6' : '#eef2ff',
                            color: isArchived ? '#9ca3af' : '#4338ca',
                            border: isArchived ? '1px solid #d1d5db' : '1px solid #4f46e5',
                            textDecoration: isArchived ? 'line-through' : 'none',
                          }}
                        >
                          {tag?.label || tagId}{isArchived && ' (비활성)'}
                          <button
                            type="button"
                            onClick={() => setEditWorkTypeTags(editWorkTypeTags.filter((id) => id !== tagId))}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: '0',
                              cursor: 'pointer',
                              color: isArchived ? '#9ca3af' : '#4338ca',
                              fontSize: '12px',
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                    {editWorkTypeTags.length === 0 && (
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>선택된 태그 없음</span>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {(project.workTypeTags || []).map((tagId) => {
                    const tag = workTypeTagMasters.find(t => t.id === tagId);
                    const isArchived = tag?.status === 'archived';
                    return tag ? (
                      <span
                        key={tagId}
                        style={{
                          padding: '2px 8px',
                          fontSize: '11px',
                          borderRadius: '12px',
                          background: isArchived ? '#f3f4f6' : '#eef2ff',
                          color: isArchived ? '#9ca3af' : '#4338ca',
                        }}
                      >
                        {tag.label}{isArchived && ' (비활성)'}
                      </span>
                    ) : null;
                  })}
                  {(!project.workTypeTags || project.workTypeTags.length === 0) && (
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>-</span>
                  )}
                </div>
              )}
            </div>
            {/* 프로젝트 분야 */}
            <div>
              <span style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>
                {PROJECT_TAG_TYPE_LABELS.projectDomain}
              </span>
              {isEditing ? (
                <div>
                  {/* V96: 드롭다운 (미선택 + active 항목만 표시) */}
                  <select
                    value=""
                    onChange={(e) => {
                      const tagId = e.target.value;
                      if (tagId && !editProjectDomainTags.includes(tagId)) {
                        setEditProjectDomainTags([...editProjectDomainTags, tagId]);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '4px 8px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '4px',
                      marginBottom: '6px',
                      fontSize: '11px',
                    }}
                  >
                    <option value="">-- 태그 추가 --</option>
                    {projectDomainTagMasters
                      .filter((tag) => tag.status === 'active' && !editProjectDomainTags.includes(tag.id))
                      .map((tag) => (
                        <option key={tag.id} value={tag.id}>
                          {tag.label}
                        </option>
                      ))}
                  </select>
                  {/* V96: 선택된 태그 칩 */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', minHeight: '20px' }}>
                    {editProjectDomainTags.map((tagId) => {
                      const tag = projectDomainTagMasters.find((t) => t.id === tagId);
                      const isArchived = tag?.status === 'archived';
                      return (
                        <span
                          key={tagId}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            padding: '2px 6px',
                            fontSize: '11px',
                            borderRadius: '12px',
                            background: isArchived ? '#f3f4f6' : '#ecfdf5',
                            color: isArchived ? '#9ca3af' : '#047857',
                            border: isArchived ? '1px solid #d1d5db' : '1px solid #059669',
                            textDecoration: isArchived ? 'line-through' : 'none',
                          }}
                        >
                          {tag?.label || tagId}{isArchived && ' (비활성)'}
                          <button
                            type="button"
                            onClick={() => setEditProjectDomainTags(editProjectDomainTags.filter((id) => id !== tagId))}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: '0',
                              cursor: 'pointer',
                              color: isArchived ? '#9ca3af' : '#047857',
                              fontSize: '12px',
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                    {editProjectDomainTags.length === 0 && (
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>선택된 태그 없음</span>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {(project.projectDomainTags || []).map((tagId) => {
                    const tag = projectDomainTagMasters.find(t => t.id === tagId);
                    const isArchived = tag?.status === 'archived';
                    return tag ? (
                      <span
                        key={tagId}
                        style={{
                          padding: '2px 8px',
                          fontSize: '11px',
                          borderRadius: '12px',
                          background: isArchived ? '#f3f4f6' : '#ecfdf5',
                          color: isArchived ? '#9ca3af' : '#047857',
                        }}
                      >
                        {tag.label}{isArchived && ' (비활성)'}
                      </span>
                    ) : null;
                  })}
                  {(!project.projectDomainTags || project.projectDomainTags.length === 0) && (
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>-</span>
                  )}
                </div>
              )}
            </div>
          </div>
      </section>

      {/* V123: 계약 기간 섹션 (정본: CreateProjectModal과 동일) */}
      {revision && (
        <section style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
            계약 기간
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="date"
              value={editContract.startDate || ''}
              onChange={(e) => {
                const newStartDate = e.target.value;
                setEditContract({ ...editContract, startDate: newStartDate });
                // V116.2: effectiveFrom 자동 동기화 (초기 리비전인 경우에만)
                if (revision?.revisionNo === 1) {
                  setEditEffectiveFrom(newStartDate);
                }
              }}
              disabled={!isEditing}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                flex: 1,
                background: isEditing ? 'white' : '#f8fafc',
              }}
            />
            <span style={{ color: '#6b7280' }}>~</span>
            <input
              type="date"
              value={editContract.endDate || ''}
              onChange={(e) => setEditContract({ ...editContract, endDate: e.target.value })}
              disabled={!isEditing}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                flex: 1,
                background: isEditing ? 'white' : '#f8fafc',
              }}
            />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>
            프로젝트 계약의 시작일과 종료일
          </p>
        </section>
      )}

      {/* V123: 계약 일자 섹션 (정본: CreateProjectModal과 동일) */}
      {/* V123.1: renew/scheduleRenew 모드에서는 기존 갱신 날짜 UI(V82)를 사용하므로 숨김 */}
      {revision && (!isEditing || editMode === 'overwrite') && (
        <section style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
            계약 일자
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                적용일 (계약 효력 발휘일)
              </label>
              <input
                type="date"
                value={isEditing ? editEffectiveFrom : (revision?.effectiveFrom || '')}
                onChange={(e) => setEditEffectiveFrom(e.target.value)}
                disabled={!isEditing || (editMode === 'overwrite' && revision?.revisionNo === 1)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  background: (!isEditing || (editMode === 'overwrite' && revision?.revisionNo === 1)) ? '#f1f5f9' : 'white',
                  color: (!isEditing || (editMode === 'overwrite' && revision?.revisionNo === 1)) ? '#64748b' : 'inherit',
                }}
              />
              {revision?.revisionNo === 1 && isEditing && (
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>
                  계약 시작일과 자동 동기화
                </p>
              )}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                계약일 (계약 실행일)
              </label>
              {/* V124: 초기 리비전에서도 renewedAt 수정 허용 (정책 변경) */}
              <input
                type="date"
                value={isEditing ? editRenewedAt : (revision?.renewedAt || '')}
                onChange={(e) => setEditRenewedAt(e.target.value)}
                disabled={!isEditing}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  background: !isEditing ? '#f1f5f9' : 'white',
                  color: !isEditing ? '#64748b' : 'inherit',
                }}
              />
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>
                계약서 작성/서명일
              </p>
            </div>
          </div>
        </section>
      )}

      {/* V123: 고객사 정보 섹션 (정본: CreateProjectModal과 동일하게 별도 섹션) */}
      {revision && (
        <section style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
            고객사 정보
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* V99.4: 고객사명은 마스터 우선, 스냅샷 fallback */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                고객사명
              </label>
              <div style={{ ...inputStyle, background: '#f1f5f9', color: '#475569' }}>
                {project.customerId && customerMap?.[project.customerId]
                  ? customerMap[project.customerId].name
                  : editCustomer.name || '-'}
              </div>
            </div>
            {/* V110: 담당자 연락처 - placeholder/검증 개선 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  담당자명
                </label>
                <input
                  type="text"
                  value={editCustomer.contactName || ''}
                  onChange={(e) => setEditCustomer({ ...editCustomer, contactName: e.target.value })}
                  disabled={!isEditing}
                  placeholder="담당자명을 입력하세요"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  연락처
                </label>
                <input
                  type="tel"
                  value={editCustomer.contactPhone || ''}
                  onChange={(e) => {
                    // V110: 숫자와 하이픈만 허용
                    const filtered = e.target.value.replace(/[^0-9-]/g, '');
                    setEditCustomer({ ...editCustomer, contactPhone: filtered });
                  }}
                  disabled={!isEditing}
                  placeholder="연락처를 입력하세요"
                  style={inputStyle}
                />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                담당자 이메일
              </label>
              <input
                type="email"
                value={editCustomer.contactEmail || ''}
                onChange={(e) => setEditCustomer({ ...editCustomer, contactEmail: e.target.value })}
                disabled={!isEditing}
                placeholder="이메일 주소를 입력하세요"
                style={inputStyle}
              />
            </div>
          </div>
        </section>
      )}

      {/* V123: 계약 정보 섹션 (정본: CreateProjectModal과 동일하게 별도 섹션) */}
      {revision && (
        <section style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
            계약 정보
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* 계약인원 */}
            <ContractEditor
                  contract={editContract}
                  onChange={setEditContract}
                  disabled={!isEditing}
                  actualStaffingByGrade={(() => {
                    // V75: actualStaffing 우선, 없으면 staffing fallback
                    if (editActualStaffing && getTotalFTE(editActualStaffing) > 0) {
                      return {
                        '특급': editActualStaffing.special,
                        '고급': editActualStaffing.senior,
                        '중급': editActualStaffing.mid,
                        '초급': editActualStaffing.junior,
                        '등급무관': editActualStaffing.any,
                      };
                    }
                    return aggregateStaffingByGrade({
                      lead: editStaffingLead,
                      members: editStaffingMembers,
                    });
                  })()}
                />
                {/* V78: 투입인원 (기존 "투입 정보" 섹션을 계약정보 내부로 이동) */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: '13px', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    투입 인원
                  </label>
                  {(project as any).isHistorical ? (
                    /* V69 (2026-05-20): 과거 프로젝트 — 등급별 인원수만 편집 (직원/리더/외부/staffAssignments UI 모두 hide). */
                    <HistoricalActualStaffingEditor
                      value={editActualStaffing}
                      onChange={setEditActualStaffing}
                      disabled={!isEditing}
                    />
                  ) : (
                    <StaffingEditor
                      // V75: 새 스키마
                      actualStaffing={editActualStaffing}
                      staffAssignments={editStaffAssignments}
                      onChangeActualStaffing={setEditActualStaffing}
                      onChangeStaffAssignments={setEditStaffAssignments}
                      // 기존 스키마 (하위호환)
                      lead={editStaffingLead}
                      members={editStaffingMembers}
                      onChangeLead={setEditStaffingLead}
                      onChangeMembers={setEditStaffingMembers}
                      disabled={!isEditing}
                      scopeOrgUnitIds={scopeOrgUnitIds}
                      scopeMode={scopeMode}
                      // V114.4: 프로젝트 투입인원 모드 (KR: KR+CN, CN: CN only)
                      forProjectStaff={true}
                    />
                  )}
            </div>
          </div>
        </section>
      )}

      {/* V123: 단가 정보 섹션 (정본: CreateProjectModal과 동일하게 별도 섹션) */}
      {revision && (
        <section style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
            단가 정보
          </h3>
          <PricingEditor
            contract={editContract}
            onChange={setEditContract}
            disabled={!isEditing}
          />
        </section>
      )}

      {/* V123: 메모 섹션 (정본: CreateProjectModal과 동일하게 별도 섹션) */}
      {revision && (
        <section style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
            메모
          </h3>
          <textarea
            value={editContract.notes || ''}
            onChange={(e) => setEditContract({ ...editContract, notes: e.target.value })}
            disabled={!isEditing}
            placeholder="메모사항을 입력하세요"
            rows={3}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              resize: 'vertical',
            }}
          />
        </section>
      )}

      {/* V82: 갱신/예약 모드일 때 날짜 입력 UI */}
      {isEditing && (editMode === 'renew' || editMode === 'scheduleRenew') && (
        <div
          style={{
            background: '#f8fafc',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid #e2e8f0',
          }}
        >
          <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600 }}>
            {editMode === 'renew' ? '계약 갱신 정보' : '계약 갱신 예약 정보'}
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* 적용일 */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                적용일 (계약 효력 발휘일) *
              </label>
              <input
                type="date"
                value={renewEffectiveFrom}
                onChange={(e) => {
                  const v = e.target.value;
                  setRenewEffectiveFrom(v);
                  // V67: scheduleRenew 모드에서 effectiveFrom 변경 시 scheduledAt 자동 동기화
                  if (editMode === 'scheduleRenew') {
                    setScheduledAt(v);
                  }
                }}
                min={(() => {
                  // V67: 예약 갱신은 effectiveFrom >= 내일 강제
                  const prevPlus1 = revision?.effectiveFrom ? (() => {
                    const d = new Date(revision.effectiveFrom);
                    d.setDate(d.getDate() + 1);
                    return d.toISOString().slice(0, 10);
                  })() : undefined;
                  if (editMode === 'scheduleRenew') {
                    const tmr = (() => {
                      const t = new Date(Date.now() + 9 * 60 * 60 * 1000 + 24 * 60 * 60 * 1000);
                      return t.toISOString().slice(0, 10);
                    })();
                    return prevPlus1 && prevPlus1 > tmr ? prevPlus1 : tmr;
                  }
                  return prevPlus1;
                })()}
                max={editMode === 'renew' ? (() => {
                  // V67: 즉시 갱신은 effectiveFrom <= 오늘 강제
                  const t = new Date(Date.now() + 9 * 60 * 60 * 1000);
                  return t.toISOString().slice(0, 10);
                })() : undefined}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                }}
              />
              {revision?.effectiveFrom && (
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>
                  현재 버전 적용일: {revision.effectiveFrom} (이후 날짜만 선택 가능)
                </p>
              )}
              {/* V67: 정책 안내 */}
              {editMode === 'renew' && (
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#4f46e5' }}>
                  계약 갱신은 즉시 적용됩니다. (오늘 또는 과거 날짜만 가능)
                </p>
              )}
              {editMode === 'scheduleRenew' && (
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#f59e0b' }}>
                  계약 갱신 예약은 미래 날짜만 가능합니다.
                </p>
              )}
            </div>
            {/* 계약일 */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                계약일 (계약 실행일) *
              </label>
              <input
                type="date"
                value={renewedAt}
                onChange={(e) => setRenewedAt(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                }}
              />
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>
                계약서 작성/서명일 (과거 날짜 가능)
              </p>
            </div>
            {/* 예약 적용일 (예약 갱신 모드에서만) — V68: 적용일과 동일 자동 동기화, 사용자 수정 불가 */}
            {editMode === 'scheduleRenew' && (
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  예약 적용일 (자동)
                </label>
                <input
                  type="date"
                  value={scheduledAt}
                  readOnly
                  tabIndex={-1}
                  aria-readonly="true"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    background: '#f1f5f9',
                    color: '#475569',
                    cursor: 'not-allowed',
                  }}
                />
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#f59e0b' }}>
                  예약 적용일은 위 적용일과 동일하게 자동 설정됩니다. 예약일 00:05(KST)에 자동 적용됩니다.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* V82: 액션 버튼 */}
      {isEditing && editMode && (
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: 'white',
            padding: '16px 0',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={handleCancelEdit}
            disabled={isMutating}
            style={{
              padding: '8px 16px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
            }}
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={isMutating}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              background: editMode === 'overwrite' ? '#059669' : (editMode === 'renew' ? '#4f46e5' : '#f59e0b'),
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {editMode === 'overwrite' && <Save size={16} />}
            {editMode === 'renew' && <RotateCw size={16} />}
            {editMode === 'scheduleRenew' && <Clock size={16} />}
            {isMutating
              ? (editMode === 'overwrite' ? '저장 중...' : (editMode === 'renew' ? '갱신 중...' : '예약 중...'))
              : (editMode === 'overwrite' ? '덮어쓰기 저장' : (editMode === 'renew' ? '갱신 (새 버전 생성)' : '예약 저장'))
            }
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// 이력 탭
// ============================================

interface HistoryTabProps {
  project: ProjectWithRevision;
  revisions: ProjectRevision[];
  selectedRevision: ProjectRevision | null;
  onSelectRevision: (rev: ProjectRevision | null) => void;
  /** V99.1: 고객사 마스터 맵 (마스터 우선 표시용) */
  customerMap?: Record<string, Customer>;
  /** V67: 태그 마스터 맵 (예약 상세 ID→라벨) */
  tagMap?: Record<string, ProjectTagMaster>;
  /** V103: 예약 조회 함수 */
  onGetReservations?: (projectId: string, statusFilter?: ProjectRenewalReservationStatus) => Promise<ProjectRenewalReservation[]>;
  /** V103: 예약 취소 함수 */
  onCancelReservation?: (reservationId: string) => Promise<any>;
  /** V116.6: 읽기 전용 모드 */
  readOnly?: boolean;
}

function HistoryTab({
  project,
  revisions,
  selectedRevision,
  onSelectRevision,
  customerMap,
  tagMap,
  onGetReservations,
  onCancelReservation,
  readOnly = false,
}: HistoryTabProps) {
  // V103: 예약 목록 상태
  const [reservations, setReservations] = useState<ProjectRenewalReservation[]>([]);
  const [reservationsLoading, setReservationsLoading] = useState(false);
  // V103: 선택된 예약 (revision과 별도)
  const [selectedReservation, setSelectedReservation] = useState<ProjectRenewalReservation | null>(null);
  // V103: 취소 처리 중 상태
  const [isCancelling, setIsCancelling] = useState(false);

  // V103: 예약 목록 로드
  useEffect(() => {
    const loadReservations = async () => {
      if (!onGetReservations) return;
      setReservationsLoading(true);
      try {
        // pending, cancelled, applied 모두 조회 (전체)
        const result = await onGetReservations(project.id);
        setReservations(result);
      } catch (err) {
        console.error('[V103] 예약 목록 로드 실패:', err);
      } finally {
        setReservationsLoading(false);
      }
    };
    loadReservations();
  }, [project.id, onGetReservations]);

  // V103: 예약 취소 핸들러
  const handleCancelReservation = async (reservation: ProjectRenewalReservation) => {
    if (!onCancelReservation) return;
    if (!window.confirm(`"V${(revisions[0]?.revisionNo || 0) + 1} (예약)" 예약을 취소하시겠습니까?`)) return;

    setIsCancelling(true);
    try {
      await onCancelReservation(reservation.id);
      // 예약 목록 갱신
      if (onGetReservations) {
        const updated = await onGetReservations(project.id);
        setReservations(updated);
        // 취소된 예약 찾아서 selectedReservation 갱신
        const cancelled = updated.find(r => r.id === reservation.id);
        if (cancelled) {
          setSelectedReservation(cancelled);
        }
      }
    } catch (err: any) {
      alert(err?.message || '예약 취소 실패');
    } finally {
      setIsCancelling(false);
    }
  };

  // V99.8: 고객사명 표시 헬퍼 (마스터 우선, 스냅샷 fallback)
  const getDisplayCustomerName = (revision?: ProjectRevision | null) => {
    if (project.customerId && customerMap?.[project.customerId]?.name) {
      return customerMap[project.customerId].name;
    }
    return revision?.customer?.name || '-';
  };

  // V103: 예약의 고객사명 표시 헬퍼
  const getReservationCustomerName = (reservation: ProjectRenewalReservation) => {
    if (project.customerId && customerMap?.[project.customerId]?.name) {
      return customerMap[project.customerId].name;
    }
    return reservation.payloadSnapshot.customer?.name || '-';
  };

  // V103: 예약 상태 배지 렌더
  const getReservationStatusBadge = (status: ProjectRenewalReservationStatus) => {
    const config: Record<ProjectRenewalReservationStatus, { bg: string; text: string; label: string }> = {
      pending: { bg: '#fef3c7', text: '#92400e', label: '예약중' },
      applying: { bg: '#dbeafe', text: '#1e40af', label: '적용중' },
      applied: { bg: '#dcfce7', text: '#166534', label: '적용됨' },
      failed: { bg: '#fee2e2', text: '#991b1b', label: '실패' },
      cancelled: { bg: '#f1f5f9', text: '#64748b', label: '예약 취소됨' },
    };
    const c = config[status];
    return (
      <span style={{ padding: '2px 8px', background: c.bg, color: c.text, borderRadius: '10px', fontSize: '11px' }}>
        {c.label}
      </span>
    );
  };

  // V103: 선택된 예약 상세 보기
  if (selectedReservation) {
    const payload = selectedReservation.payloadSnapshot;
    const nextRevisionNo = (revisions[0]?.revisionNo || 0) + 1;

    return (
      <div style={{ padding: '20px' }}>
        {/* 뒤로 가기 */}
        <button
          onClick={() => setSelectedReservation(null)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 0',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: '#4f46e5',
            fontSize: '13px',
            marginBottom: '16px',
          }}
        >
          ← 이력 목록으로 돌아가기
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
            V{nextRevisionNo} 예약
          </h3>
          {getReservationStatusBadge(selectedReservation.status)}
        </div>

        {/* 예약 상태별 안내 메시지 */}
        {selectedReservation.status === 'pending' && (
          <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#92400e' }}>
            이 예약은 {selectedReservation.scheduledAt}에 자동 적용될 예정입니다.
          </div>
        )}
        {selectedReservation.status === 'cancelled' && (
          <div style={{ background: '#f1f5f9', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#64748b' }}>
            이 예약은 취소되었습니다.
          </div>
        )}
        {selectedReservation.status === 'applied' && (
          <div style={{ background: '#dcfce7', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#166534' }}>
            이 예약은 이미 적용되어 리비전이 생성되었습니다.
          </div>
        )}

        {/* 예약 정보 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <InfoRow label="예약 적용일" value={selectedReservation.scheduledAt} />
            <InfoRow label="적용일" value={payload.effectiveFrom} />
          </div>

          {/* V100: 예약된 상태 변경 표시 */}
          {payload.projectStatus && (
            <div style={{ background: '#f0f9ff', padding: '12px', borderRadius: '8px', border: '1px solid #bae6fd' }}>
              <span style={{ fontSize: '13px', color: '#0369a1' }}>
                예약된 상태 변경: <strong>{PROJECT_STATUS_LABELS[payload.projectStatus]}</strong>
              </span>
            </div>
          )}

          {/* V275: 예약된 마스터 정보 변경 표시 */}
          {payload.masterSnapshot && (
            <section style={{ background: '#fefce8', borderRadius: '8px', padding: '16px', border: '1px solid #fde68a' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600, color: '#854d0e' }}>
                프로젝트 기본정보 변경
              </h4>
              {payload.masterSnapshot.name !== undefined && (
                <InfoRow label="프로젝트명" value={payload.masterSnapshot.name} />
              )}
              {payload.masterSnapshot.description !== undefined && (
                <InfoRow label="설명" value={payload.masterSnapshot.description || '(삭제)'} />
              )}
              {payload.masterSnapshot.workTypeTags !== undefined && (
                <div style={{ marginTop: '4px' }}>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>{PROJECT_TAG_TYPE_LABELS.workType}: </span>
                  <span style={{ fontSize: '12px', color: '#1e293b' }}>
                    {payload.masterSnapshot.workTypeTags.length > 0
                      ? payload.masterSnapshot.workTypeTags
                          .map((tagId: string) => tagMap?.[tagId]?.label ?? '(삭제된 태그)')
                          .join(', ')
                      : '(없음)'}
                  </span>
                </div>
              )}
              {payload.masterSnapshot.projectDomainTags !== undefined && (
                <div style={{ marginTop: '4px' }}>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>{PROJECT_TAG_TYPE_LABELS.projectDomain}: </span>
                  <span style={{ fontSize: '12px', color: '#1e293b' }}>
                    {payload.masterSnapshot.projectDomainTags.length > 0
                      ? payload.masterSnapshot.projectDomainTags
                          .map((tagId: string) => tagMap?.[tagId]?.label ?? '(삭제된 태그)')
                          .join(', ')
                      : '(없음)'}
                  </span>
                </div>
              )}
            </section>
          )}

          {/* 고객사 정보 */}
          <section style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600, color: '#475569' }}>
              고객사 정보
            </h4>
            <InfoRow label="고객사명" value={getReservationCustomerName(selectedReservation)} />
            {(payload.customer?.contactName || payload.customer?.contactPhone || payload.customer?.contactEmail) && (
              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {payload.customer.contactName && <InfoRow label="담당자" value={payload.customer.contactName} />}
                {payload.customer.contactPhone && <InfoRow label="연락처" value={payload.customer.contactPhone} />}
                {payload.customer.contactEmail && <InfoRow label="이메일" value={payload.customer.contactEmail} />}
              </div>
            )}
          </section>

          {/* 계약 정보 */}
          <section style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600, color: '#475569' }}>
              계약 정보
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <InfoRow label="시작일" value={payload.contract?.startDate} />
              <InfoRow label="종료일" value={payload.contract?.endDate} />
            </div>
            {payload.contract?.contractedResources && Array.isArray(payload.contract.contractedResources) && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>등급별 계약 인원</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {payload.contract.contractedResources
                    .filter((r: ContractedResource) => r.headcount > 0)
                    .map((r: ContractedResource) => (
                      <span
                        key={r.grade}
                        style={{ padding: '4px 10px', background: '#e0e7ff', color: '#3730a3', borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}
                      >
                        {r.grade}: {formatMM(r.headcount)} MM
                      </span>
                    ))}
                </div>
              </div>
            )}
            {payload.contract && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px dashed #e2e8f0' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>총단가 (원/월)</span>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e40af' }}>
                  {calculateTotalContractPrice(payload.contract).toLocaleString('ko-KR')}
                </span>
              </div>
            )}
          </section>

          {/* 투입 정보 */}
          <section style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600, color: '#475569' }}>
              투입 정보
            </h4>
            {payload.staffAssignments && payload.staffAssignments.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {payload.staffAssignments.map((assignment) => {
                  const isLead = assignment.role === 'TL';
                  return (
                    <div
                      key={assignment.userId}
                      style={{
                        padding: '8px 12px',
                        background: isLead ? '#f0f9ff' : 'white',
                        borderRadius: '6px',
                        border: isLead ? '1px solid #bae6fd' : '1px solid #e2e8f0',
                        fontSize: '13px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontWeight: 500, color: isLead ? '#0369a1' : '#1e293b' }}>
                        {assignment.nameSnapshot || assignment.userId}
                        {isLead && (
                          <span style={{ marginLeft: '6px', padding: '1px 6px', background: '#dbeafe', color: '#1e40af', borderRadius: '4px', fontSize: '10px' }}>리드</span>
                        )}
                      </span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ color: '#64748b' }}>{assignment.grade}</span>
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>{formatMM(assignment.fte)} FTE</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px' }}>실투입 인원 없음</p>
            )}
          </section>

          {/* 예약 취소 버튼 (pending 상태일 때만, readOnly 모드에서는 숨김) */}
          {!readOnly && selectedReservation.status === 'pending' && onCancelReservation && (
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
              <button
                onClick={() => handleCancelReservation(selectedReservation)}
                disabled={isCancelling}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ef4444',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#ef4444',
                  fontWeight: 500,
                  cursor: isCancelling ? 'not-allowed' : 'pointer',
                  opacity: isCancelling ? 0.6 : 1,
                }}
              >
                {isCancelling ? '취소 처리 중...' : '예약 취소'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 선택된 리비전 상세 보기
  if (selectedRevision) {
    const isCurrent = selectedRevision.id === project.currentRevisionId;

    return (
      <div style={{ padding: '20px' }}>
        {/* 뒤로 가기 */}
        <button
          onClick={() => onSelectRevision(null)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 0',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: '#4f46e5',
            fontSize: '13px',
            marginBottom: '16px',
          }}
        >
          ← 이력 목록으로 돌아가기
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
            리비전 v{selectedRevision.revisionNo}
          </h3>
          {isCurrent ? (
            <span
              style={{
                padding: '2px 8px',
                background: '#3b82f6',
                color: 'white',
                borderRadius: '10px',
                fontSize: '11px',
              }}
            >
              현재 적용 중
            </span>
          ) : (
            <span
              style={{
                padding: '2px 8px',
                background: '#f1f5f9',
                color: '#64748b',
                borderRadius: '10px',
                fontSize: '11px',
              }}
            >
              과거 버전
            </span>
          )}
        </div>

        {!isCurrent && (
          <div
            style={{
              background: '#fef3c7',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#92400e',
            }}
          >
            이 리비전은 과거 버전입니다. 읽기 전용으로 표시됩니다.
          </div>
        )}

        {/* 리비전 상세 (읽기 전용) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <InfoRow label="적용일" value={selectedRevision.effectiveFrom} />

          {/* V99.3: 고객사 정보 + 담당자 연락처 */}
          <section style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600, color: '#475569' }}>
              고객사 정보
            </h4>
            <InfoRow
              label="고객사명"
              value={
                project.customerId && customerMap?.[project.customerId]
                  ? customerMap[project.customerId].name
                  : selectedRevision.customer?.name || '-'
              }
            />
            {/* V99.3: 담당자 연락처 표시 */}
            {(selectedRevision.customer?.contactName ||
              selectedRevision.customer?.contactPhone ||
              selectedRevision.customer?.contactEmail) && (
              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {selectedRevision.customer.contactName && (
                  <InfoRow label="담당자" value={selectedRevision.customer.contactName} />
                )}
                {selectedRevision.customer.contactPhone && (
                  <InfoRow label="연락처" value={selectedRevision.customer.contactPhone} />
                )}
                {selectedRevision.customer.contactEmail && (
                  <InfoRow label="이메일" value={selectedRevision.customer.contactEmail} />
                )}
              </div>
            )}
          </section>

          {/* 계약 (V140.3) */}
          <section style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600, color: '#475569' }}>
              계약 정보
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <InfoRow label="시작일" value={selectedRevision.contract?.startDate} />
              <InfoRow label="종료일" value={selectedRevision.contract?.endDate} />
            </div>
            {/* 등급별 계약 인원 */}
            {selectedRevision.contract?.contractedResources && Array.isArray(selectedRevision.contract.contractedResources) && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>등급별 계약 인원</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {selectedRevision.contract.contractedResources
                    .filter((r: ContractedResource) => r.headcount > 0)
                    .map((r: ContractedResource) => (
                      <span
                        key={r.grade}
                        style={{
                          padding: '4px 10px',
                          background: '#e0e7ff',
                          color: '#3730a3',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        {r.grade}: {formatMM(r.headcount)} MM
                      </span>
                    ))}
                  {selectedRevision.contract.contractedResources.every((r: ContractedResource) => r.headcount === 0) && (
                    <span style={{ color: '#94a3b8', fontSize: '12px' }}>계약 인원 없음</span>
                  )}
                </div>
              </div>
            )}
            {/* 총단가 */}
            {selectedRevision.contract && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px dashed #e2e8f0' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>총단가 (원/월)</span>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e40af' }}>
                  {calculateTotalContractPrice(selectedRevision.contract).toLocaleString('ko-KR')}
                </span>
              </div>
            )}
          </section>

          {/* V93: 투입 인력 (V75 SSoT: staffAssignments 사용) */}
          <section style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600, color: '#475569' }}>
              투입 정보
            </h4>
            {/* V94: 프로젝트 리드 표시 - staffAssignments에서 role='TL' 찾기 */}
            {(() => {
              const leadAssignment = selectedRevision.staffAssignments?.find(a => a.role === 'TL');
              return (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                    프로젝트 리드
                  </div>
                  {leadAssignment ? (
                    <div
                      style={{
                        padding: '8px 12px',
                        background: '#f0f9ff',
                        borderRadius: '6px',
                        border: '1px solid #bae6fd',
                        fontSize: '13px',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ fontWeight: 500, color: '#0369a1' }}>
                        {leadAssignment.nameSnapshot || leadAssignment.userId}
                      </span>
                      <span style={{ color: '#64748b' }}>
                        {leadAssignment.grade}
                      </span>
                    </div>
                  ) : (
                    <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px' }}>리드 없음</p>
                  )}
                </div>
              );
            })()}
            {/* V94: 실투입 인원 표시 - staffAssignments SSoT 사용 */}
            <div>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                실투입 인원 ({selectedRevision.staffAssignments?.length || 0}명)
              </div>
              {selectedRevision.staffAssignments && selectedRevision.staffAssignments.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {selectedRevision.staffAssignments.map((assignment) => {
                    const isLead = assignment.role === 'TL';
                    return (
                      <div
                        key={assignment.userId}
                        style={{
                          padding: '8px 12px',
                          background: isLead ? '#f0f9ff' : 'white',
                          borderRadius: '6px',
                          border: isLead ? '1px solid #bae6fd' : '1px solid #e2e8f0',
                          fontSize: '13px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ fontWeight: 500, color: isLead ? '#0369a1' : '#1e293b' }}>
                          {assignment.nameSnapshot || assignment.userId}
                          {isLead && (
                            <span style={{ marginLeft: '6px', padding: '1px 6px', background: '#dbeafe', color: '#1e40af', borderRadius: '4px', fontSize: '10px' }}>리드</span>
                          )}
                        </span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ color: '#64748b' }}>{assignment.grade}</span>
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>{formatMM(assignment.fte)} FTE</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px' }}>실투입 인원 없음</p>
              )}
            </div>
          </section>
        </div>
      </div>
    );
  }

  // V103: 통합 이력 목록 (revisions + reservations)
  // 예약은 최상단에 표시, 그 다음 revisions (최신순)
  const nextRevisionNo = (revisions[0]?.revisionNo || 0) + 1;

  // applied 상태 예약은 이미 revision이 생성되었으므로 숨김 (또는 표시해도 됨 - 일단 표시)
  const visibleReservations = reservations.filter(r => r.status !== 'applied');

  const hasItems = revisions.length > 0 || visibleReservations.length > 0;

  return (
    <div style={{ padding: '20px' }}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
        리비전 이력 {reservationsLoading && <span style={{ fontWeight: 400, color: '#94a3b8' }}>(로딩중...)</span>}
      </h3>

      {!hasItems ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
          이력이 없습니다.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* V103: 예약 항목들 (상단에 표시) */}
          {visibleReservations.map((reservation) => {
            const isPending = reservation.status === 'pending';
            const isCancelled = reservation.status === 'cancelled';

            return (
              <div
                key={`reservation-${reservation.id}`}
                onClick={() => setSelectedReservation(reservation)}
                style={{
                  padding: '16px',
                  background: isPending ? '#fffbeb' : '#f8fafc',
                  borderRadius: '8px',
                  border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  opacity: isCancelled ? 0.7 : 1,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>V{nextRevisionNo} (예약)</span>
                    {getReservationStatusBadge(reservation.status)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#64748b', fontSize: '12px' }}>
                    <Calendar size={12} />
                    예약일: {reservation.scheduledAt}
                  </div>
                </div>

                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  <span>고객: {getReservationCustomerName(reservation)}</span>
                  <span style={{ margin: '0 8px' }}>|</span>
                  <span>
                    계약: {reservation.payloadSnapshot.contract?.startDate} ~ {reservation.payloadSnapshot.contract?.endDate}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  <span>
                    계약: {formatMM(reservation.payloadSnapshot.contract ? getTotalContractedHeadcount(reservation.payloadSnapshot.contract) : 0)} MM
                    <GradeBreakdownTokens
                      tokens={getGradeTokensForContract(reservation.payloadSnapshot.contract?.contractStaffing)}
                    />
                  </span>
                  <span style={{ margin: '0 8px' }}>|</span>
                  <span style={{ color: '#1e40af', fontWeight: 500 }}>
                    총단가: {reservation.payloadSnapshot.contract ? calculateTotalContractPrice(reservation.payloadSnapshot.contract).toLocaleString('ko-KR') : 0}원
                  </span>
                </div>

                <div style={{ marginTop: '8px', textAlign: 'right' }}>
                  <span style={{ fontSize: '12px', color: isPending ? '#f59e0b' : '#4f46e5' }}>
                    클릭하여 상세 보기 →
                  </span>
                </div>
              </div>
            );
          })}

          {/* 기존 리비전 항목들 */}
          {revisions.map((rev) => {
            const isCurrent = rev.id === project.currentRevisionId;

            return (
              <div
                key={rev.id}
                onClick={() => onSelectRevision(rev)}
                style={{
                  padding: '16px',
                  background: isCurrent ? '#f0f9ff' : '#f8fafc',
                  borderRadius: '8px',
                  border: isCurrent ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>v{rev.revisionNo}</span>
                    {isCurrent && (
                      <span
                        style={{
                          padding: '2px 8px',
                          background: '#3b82f6',
                          color: 'white',
                          borderRadius: '10px',
                          fontSize: '11px',
                        }}
                      >
                        현재
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#64748b', fontSize: '12px' }}>
                    <Clock size={12} />
                    적용일: {rev.effectiveFrom}
                  </div>
                </div>

                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  {/* V99.8: 고객사명 마스터 우선 표시 */}
                  <span>고객: {getDisplayCustomerName(rev)}</span>
                  <span style={{ margin: '0 8px' }}>|</span>
                  <span>
                    계약: {rev.contract?.startDate} ~ {rev.contract?.endDate}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  {/* V89.1: 계약 MM + 등급 요약 (무 포함) */}
                  <span>
                    계약: {formatMM(rev.contract ? getTotalContractedHeadcount(rev.contract) : 0)} MM
                    <GradeBreakdownTokens
                      tokens={getGradeTokensForContract(rev.contract?.contractStaffing)}
                    />
                  </span>
                  <span style={{ margin: '0 8px' }}>|</span>
                  {/* V89.1: 실투입 MM + 등급 요약 (무 제외, 레거시 any→초급) + 초과 배지 */}
                  <span>
                    실투입: {formatMM(getTotalActualStaffing(rev))} MM
                    <GradeBreakdownTokens
                      tokens={getGradeTokensForActual(rev.actualStaffing)}
                    />
                    <OveruseBadge
                      tokens={getOveruseTokens(
                        rev.actualStaffing,
                        rev.contract?.contractStaffing
                      )}
                    />
                  </span>
                  <span style={{ margin: '0 8px' }}>|</span>
                  <span style={{ color: '#1e40af', fontWeight: 500 }}>
                    총단가: {rev.contract ? calculateTotalContractPrice(rev.contract).toLocaleString('ko-KR') : 0}원
                  </span>
                </div>

                <div style={{ marginTop: '8px', textAlign: 'right' }}>
                  <span style={{ fontSize: '12px', color: '#4f46e5' }}>
                    클릭하여 상세 보기 →
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 읽기 전용 정보 행
function InfoRow({ label, value }: { label: string; value?: string | number }) {
  return (
    <div style={{ fontSize: '13px' }}>
      <span style={{ color: '#64748b' }}>{label}: </span>
      <span style={{ color: '#1e293b' }}>{value || '-'}</span>
    </div>
  );
}

// ============================================
// 직원 선택 모달 (V140.2)
// ============================================

interface StaffPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (employee: Employee) => void;
  title?: string;
  excludeIds?: string[];
  /** V140.3: 조직관리 스코프 조직 ID 목록 */
  scopeOrgUnitIds?: string[];
  /** V140.3: 스코프 모드 */
  scopeMode?: 'SELF' | 'SUBTREE' | 'ALL';
  /** V114.4: 프로젝트 투입인원 모드 (true: org scope 무시, company 기반 필터) */
  forProjectStaff?: boolean;
}

// V69 (2026-05-19): 외부/기타 인력 생성 모달
// users 컬렉션 미사용 — staffAssignments 배열에만 isExternal=true 로 저장
function ExternalStaffCreateModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  // V69 (2026-05-20): grade 필수 입력. 등급무관 제외 (특급/고급/중급/초급).
  onSubmit: (input: { externalName: string; externalOrgName: string; grade: StaffGrade }) => void;
}) {
  const [externalOrgName, setExternalOrgName] = useState('');
  const [externalName, setExternalName] = useState('');
  // V69 (2026-05-20): grade 필수 — 기본값 빈 문자열로 선택 강제
  const [grade, setGrade] = useState<StaffGrade | ''>('');

  useEffect(() => {
    if (isOpen) {
      setExternalOrgName('');
      setExternalName('');
      setGrade('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canSubmit =
    externalOrgName.trim().length > 0 &&
    externalName.trim().length > 0 &&
    grade !== '';

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (!grade) return;  // TS narrowing 안전 (grade: StaffGrade | '')
    onSubmit({
      externalName: externalName.trim(),
      externalOrgName: externalOrgName.trim(),
      grade,
    });
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          padding: '20px',
          width: '380px',
          maxWidth: '90vw',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>
          실투입 인원 생성 (외부/기타)
        </div>
        <div style={{ fontSize: '12px', color: '#6b7280' }}>
          소속명과 이름, 등급을 입력하세요. DB 직원 등록 없이 본 프로젝트에만 등록됩니다.<br />
          기본값: 1MM, 프로젝트 기간 동안 투입.
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#374151', marginBottom: '4px' }}>
            소속명 <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            value={externalOrgName}
            onChange={(e) => setExternalOrgName(e.target.value)}
            placeholder="예: 협력사명 또는 프리랜서"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '13px',
              boxSizing: 'border-box',
            }}
            autoFocus
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#374151', marginBottom: '4px' }}>
            이름 <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            value={externalName}
            onChange={(e) => setExternalName(e.target.value)}
            placeholder="외부 인력 이름"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '13px',
              boxSizing: 'border-box',
            }}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) handleSubmit(); }}
          />
        </div>
        {/* V69 (2026-05-20): 등급 선택 필수 (등급무관 제외) */}
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#374151', marginBottom: '4px' }}>
            등급 <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select
            value={grade}
            onChange={(e) => setGrade(e.target.value as StaffGrade | '')}
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '13px',
              boxSizing: 'border-box',
              background: 'white',
            }}
          >
            <option value="" disabled>등급을 선택하세요</option>
            {STAFF_GRADES_NO_ANY.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '4px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '6px 14px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              padding: '6px 14px',
              border: '1px solid #059669',
              borderRadius: '4px',
              background: canSubmit ? '#059669' : '#d1d5db',
              color: 'white',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontSize: '13px',
            }}
          >
            추가
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function StaffPickerModal({
  isOpen,
  onClose,
  onSelect,
  title = '직원 선택',
  excludeIds = [],
  scopeOrgUnitIds,
  scopeMode,
  forProjectStaff = false,
}: StaffPickerModalProps) {
  const [searchText, setSearchText] = useState('');

  // V72: forProjectStaff 모드는 전체 fetch 후 FE substring 검색
  // - BE 호출 시 searchText 미전달 → 회사 스코프 active 직원 전체 1회 fetch
  // - 다른 모드(forProjectStaff=false)는 기존 동작 유지
  const { employees, isLoading, error } = useEmployees(
    forProjectStaff
      ? {
          status: 'active',
          forProjectStaff: true,
        }
      : {
          searchText,
          status: 'active',
          orgUnitIds: scopeOrgUnitIds,
          scopeMode: scopeMode,
        }
  );

  // V72: debounce 200ms (forProjectStaff 모드의 FE 필터 렌더 최적화)
  const [debouncedSearch, setDebouncedSearch] = useState(searchText);
  useEffect(() => {
    if (!forProjectStaff) {
      setDebouncedSearch(searchText);
      return;
    }
    const t = setTimeout(() => setDebouncedSearch(searchText), 200);
    return () => clearTimeout(t);
  }, [searchText, forProjectStaff]);

  // V72: 정규화 substring 검색 (대소문자 무시 + 공백 제거)
  const filteredEmployees = useMemo(() => {
    const normalize = (s?: string) => (s || '').toLowerCase().replace(/\s+/g, '');
    const q = forProjectStaff ? normalize(debouncedSearch) : '';
    return employees.filter((e) => {
      if (excludeIds.includes(e.id)) return false;
      if (!forProjectStaff) return true; // 다른 모드는 BE 검색 결과 그대로
      if (!q) return true;
      return (
        normalize(e.nameKo).includes(q) ||
        normalize(e.employeeNumber).includes(q)
      );
    });
  }, [employees, excludeIds, debouncedSearch, forProjectStaff]);

  if (!isOpen) return null;

  return (
    <ModalOverlay
      onClose={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1200,
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '500px',
          // V72: 모달 높이 고정 — 검색 결과 수에 따른 흔들림 방지
          height: '70vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 검색 */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
              }}
            />
            <input
              type="text"
              placeholder="이름, 사번으로 검색..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
              }}
            />
          </div>
        </div>

        {/* 목록 — V72: 2글자 미만 가드 제거, 1글자부터 substring 검색 가능 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {isLoading && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
              로딩 중...
            </div>
          )}

          {error && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#dc2626' }}>
              오류: {error.message}
            </div>
          )}

          {!isLoading && !error && filteredEmployees.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
              <Users size={32} style={{ marginBottom: '8px' }} />
              <p style={{ margin: 0 }}>검색 결과가 없습니다.</p>
            </div>
          )}

          {!isLoading && !error && filteredEmployees.map((emp) => (
            <button
              key={emp.id}
              onClick={() => onSelect(emp)}
              style={{
                width: '100%',
                padding: '12px 20px',
                border: 'none',
                background: 'white',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
              }}
            >
              <div>
                <div style={{ fontWeight: 500, fontSize: '14px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {emp.nameKo}
                  {/* V114.4: CN 직원 라벨 표시 */}
                  {emp.companyCode === 'CN' && (
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: '#dc2626',
                        background: '#fef2f2',
                        padding: '1px 5px',
                        borderRadius: '3px',
                        border: '1px solid #fecaca',
                      }}
                    >
                      CN
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  {emp.employeeNumber || '-'} | {emp.orgUnitName || '-'}
                </div>
              </div>
              <ChevronRight size={16} style={{ color: '#94a3b8' }} />
            </button>
          ))}
        </div>

        {/* 푸터 */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e2e8f0',
            textAlign: 'right',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            닫기
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// 계약 정보 편집 컴포넌트 (V140.3, V70 개선)
// V70: 계약인원/단가 섹션 분리, 통단가/할인가 추가
// ============================================

interface ContractEditorProps {
  contract: ProjectContract;
  onChange: (contract: ProjectContract) => void;
  disabled?: boolean;
  /** 비교 표시용: 실투입 인원 등급별 집계 */
  actualStaffingByGrade?: Record<StaffGrade, number>;
}

function ContractEditor({
  contract,
  onChange,
  disabled = false,
  actualStaffingByGrade,
}: ContractEditorProps) {
  // V75: 등급별 계약 인원 맵 (빠른 조회용)
  // contractStaffing 우선, 없으면 contractedResources fallback
  const contractedByGrade = useMemo(() => {
    const map: Record<StaffGrade, number> = {
      '특급': 0, '고급': 0, '중급': 0, '초급': 0, '등급무관': 0,
    };

    // V75: contractStaffing 우선
    if (contract.contractStaffing) {
      map['특급'] = contract.contractStaffing.special || 0;
      map['고급'] = contract.contractStaffing.senior || 0;
      map['중급'] = contract.contractStaffing.mid || 0;
      map['초급'] = contract.contractStaffing.junior || 0;
      map['등급무관'] = contract.contractStaffing.any || 0;
      return map;
    }

    // 기존 방식 fallback
    for (const res of contract.contractedResources || []) {
      map[res.grade] = res.headcount;
    }
    return map;
  }, [contract.contractStaffing, contract.contractedResources]);

  // V75/V77: 계약 인원 변경 (0.5 단위)
  // V77: contractStaffing만 SSoT, contractedResources 전송 금지
  const handleHeadcountChange = (grade: StaffGrade, value: number) => {
    const newContractStaffing: GradeBasedFTE = {
      special: grade === '특급' ? Math.max(0, value) : (contractedByGrade['특급'] || 0),
      senior: grade === '고급' ? Math.max(0, value) : (contractedByGrade['고급'] || 0),
      mid: grade === '중급' ? Math.max(0, value) : (contractedByGrade['중급'] || 0),
      junior: grade === '초급' ? Math.max(0, value) : (contractedByGrade['초급'] || 0),
      any: grade === '등급무관' ? Math.max(0, value) : (contractedByGrade['등급무관'] || 0),
    };

    // V77: contractStaffing만 전송, contractedResources 제거
    const { contractedResources: _removed, ...restContract } = contract;
    onChange({
      ...restContract,
      contractStaffing: newContractStaffing,
    });
  };

  // 날짜 변경
  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    onChange({
      ...contract,
      [field]: value,
    });
  };

  // 총 계약 인원
  const totalContractedHeadcount = getTotalContractedHeadcount(contract);

  // 실투입 총 인원 (비교용)
  const totalActualHeadcount = actualStaffingByGrade
    ? Object.values(actualStaffingByGrade).reduce((sum, val) => sum + val, 0)
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* V122: 계약 기간은 CreateProjectModal에서 직접 렌더링 (계약 일자 위로 이동) */}

      {/* V75: 섹션 1 - 계약 인원 */}
      <div>
        <label style={{ fontWeight: 600, fontSize: '13px', color: '#374151', marginBottom: '8px', display: 'block' }}>
          계약 인원
        </label>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          {/* 헤더 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: actualStaffingByGrade ? '100px 1fr 1fr' : '100px 1fr',
              background: '#f9fafb',
              padding: '10px 12px',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '12px',
              fontWeight: 600,
              color: '#6b7280',
            }}
          >
            <span>등급</span>
            <span style={{ textAlign: 'center' }}>계약</span>
            {actualStaffingByGrade && <span style={{ textAlign: 'center' }}>실투입</span>}
          </div>

          {/* 등급별 행 */}
          {STAFF_GRADES.map((grade, idx) => {
            const headcount = contractedByGrade[grade] || 0;
            const actualCount = actualStaffingByGrade?.[grade] || 0;
            const hasDiff = actualStaffingByGrade && actualCount !== headcount;

            return (
              <div
                key={grade}
                style={{
                  display: 'grid',
                  gridTemplateColumns: actualStaffingByGrade ? '100px 1fr 1fr' : '100px 1fr',
                  padding: '8px 12px',
                  borderBottom: idx < STAFF_GRADES.length - 1 ? '1px solid #e5e7eb' : 'none',
                  alignItems: 'center',
                  background: hasDiff ? '#fef3c7' : 'white',
                }}
              >
                <span style={{ fontWeight: 500, fontSize: '13px' }}>{grade}</span>
                <div style={{ textAlign: 'center' }}>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={headcount}
                    onChange={(e) => handleHeadcountChange(grade, parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    style={{
                      width: '70px',
                      padding: '6px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '13px',
                      textAlign: 'center',
                    }}
                  />
                  <span style={{ marginLeft: '4px', fontSize: '13px', color: '#6b7280' }}>MM</span>
                </div>
                {actualStaffingByGrade && (
                  <div style={{ textAlign: 'center' }}>
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: '13px',
                        color: hasDiff ? (actualCount > headcount ? '#dc2626' : '#059669') : '#374151',
                      }}
                    >
                      {actualCount}
                      {hasDiff && (
                        <span style={{ fontSize: '11px', marginLeft: '2px' }}>
                          ({actualCount > headcount ? '+' : ''}{actualCount - headcount})
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          {/* 합계 행 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: actualStaffingByGrade ? '100px 1fr 1fr' : '100px 1fr',
              padding: '10px 12px',
              background: '#f3f4f6',
              borderTop: '2px solid #e5e7eb',
              fontWeight: 600,
            }}
          >
            <span style={{ fontSize: '13px' }}>합계</span>
            <span style={{ textAlign: 'center', fontSize: '13px' }}>{formatMM(totalContractedHeadcount)} MM</span>
            {actualStaffingByGrade && (
              <span
                style={{
                  textAlign: 'center',
                  fontSize: '13px',
                  color: totalActualHeadcount !== totalContractedHeadcount
                    ? (totalActualHeadcount > totalContractedHeadcount ? '#dc2626' : '#059669')
                    : '#374151',
                }}
              >
                {formatMM(totalActualHeadcount)} MM
              </span>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

// ============================================
// V78: 단가 정보 편집 컴포넌트 (계약정보에서 분리)
// ============================================

interface PricingEditorProps {
  contract: ProjectContract;
  onChange: (contract: ProjectContract) => void;
  disabled?: boolean;
}

/**
 * V78: 단가 정보 편집 컴포넌트
 *
 * 계약정보에서 분리된 단가 관련 UI:
 * - 등급별 단가
 * - 통단가
 * - 할인가
 * - 총단가
 */
function PricingEditor({
  contract,
  onChange,
  disabled = false,
}: PricingEditorProps) {
  // V75: 등급별 계약 인원 맵 (금액 계산용)
  const contractedByGrade = useMemo(() => {
    const map: Record<StaffGrade, number> = {
      '특급': 0, '고급': 0, '중급': 0, '초급': 0, '등급무관': 0,
    };
    if (contract.contractStaffing) {
      map['특급'] = contract.contractStaffing.special || 0;
      map['고급'] = contract.contractStaffing.senior || 0;
      map['중급'] = contract.contractStaffing.mid || 0;
      map['초급'] = contract.contractStaffing.junior || 0;
      map['등급무관'] = contract.contractStaffing.any || 0;
      return map;
    }
    for (const res of contract.contractedResources || []) {
      map[res.grade] = res.headcount;
    }
    return map;
  }, [contract.contractStaffing, contract.contractedResources]);

  // 등급별 소계 계산
  const gradeSubtotal = useMemo(() => calculateGradeSubtotal(contract), [contract]);

  // 총단가 계산
  const totalPrice = useMemo(() => calculateTotalContractPrice(contract), [contract]);

  // 단가 변경
  const handlePriceChange = (grade: StaffGrade, value: number) => {
    onChange({
      ...contract,
      unitPricesByGrade: {
        ...contract.unitPricesByGrade,
        [grade]: Math.max(0, value),
      },
    });
  };

  // 통단가 변경
  const handleFlatPriceChange = (value: number) => {
    onChange({
      ...contract,
      flatPrice: Math.max(0, value),
    });
  };

  // 할인가 변경
  const handleDiscountChange = (value: number) => {
    onChange({
      ...contract,
      discount: Math.max(0, value),
    });
  };

  // 숫자 포맷 (천 단위 콤마)
  const formatNumber = (num: number) => num.toLocaleString('ko-KR');

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
      {/* 등급별 단가 헤더 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '100px 1fr 1fr',
          background: '#f9fafb',
          padding: '10px 12px',
          borderBottom: '1px solid #e5e7eb',
          fontSize: '12px',
          fontWeight: 600,
          color: '#6b7280',
        }}
      >
        <span>등급</span>
        <span style={{ textAlign: 'right' }}>단가 (원/월)</span>
        <span style={{ textAlign: 'right' }}>합산금액</span>
      </div>

      {/* 등급별 단가 행 */}
      {STAFF_GRADES.map((grade, idx) => {
        const headcount = contractedByGrade[grade] || 0;
        const price = contract.unitPricesByGrade[grade] || 0;
        const subtotal = headcount * price;

        return (
          <div
            key={grade}
            style={{
              display: 'grid',
              gridTemplateColumns: '100px 1fr 1fr',
              padding: '8px 12px',
              borderBottom: idx < STAFF_GRADES.length - 1 ? '1px solid #e5e7eb' : 'none',
              alignItems: 'center',
              background: 'white',
            }}
          >
            <span style={{ fontWeight: 500, fontSize: '13px' }}>{grade}</span>
            <div style={{ textAlign: 'right' }}>
              <input
                type="number"
                min={0}
                step={100000}
                value={price}
                onChange={(e) => handlePriceChange(grade, parseInt(e.target.value) || 0)}
                disabled={disabled}
                style={{
                  width: '130px',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '13px',
                  textAlign: 'right',
                }}
              />
            </div>
            <span style={{ textAlign: 'right', fontSize: '13px', color: '#374151', whiteSpace: 'nowrap' }}>
              {formatNumber(subtotal)}
              {headcount > 0 && (
                <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '4px' }}>
                  ({headcount}명)
                </span>
              )}
            </span>
          </div>
        );
      })}

      {/* 등급별 소계 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '100px 1fr 1fr',
          padding: '10px 12px',
          background: '#f9fafb',
          borderTop: '1px solid #e5e7eb',
          fontWeight: 500,
        }}
      >
        <span style={{ fontSize: '13px', color: '#6b7280' }}>등급별 소계</span>
        <span></span>
        <span style={{ textAlign: 'right', fontSize: '13px', color: '#374151' }}>
          {formatNumber(gradeSubtotal)}
        </span>
      </div>

      {/* 통단가 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '100px 1fr 1fr',
          padding: '10px 12px',
          background: 'white',
          borderTop: '1px solid #e5e7eb',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: 500, color: '#059669' }}>+ 통단가</span>
        <div style={{ textAlign: 'right' }}>
          <input
            type="number"
            min={0}
            step={100000}
            value={contract.flatPrice ?? 0}
            onChange={(e) => handleFlatPriceChange(parseInt(e.target.value) || 0)}
            disabled={disabled}
            style={{
              width: '130px',
              padding: '6px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '13px',
              textAlign: 'right',
            }}
          />
        </div>
        <span style={{ textAlign: 'right', fontSize: '13px', color: '#059669' }}>
          + {formatNumber(contract.flatPrice ?? 0)}
        </span>
      </div>

      {/* 할인가 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '100px 1fr 1fr',
          padding: '10px 12px',
          background: 'white',
          borderTop: '1px solid #e5e7eb',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: 500, color: '#dc2626' }}>- 할인가</span>
        <div style={{ textAlign: 'right' }}>
          <input
            type="number"
            min={0}
            step={100000}
            value={contract.discount ?? 0}
            onChange={(e) => handleDiscountChange(parseInt(e.target.value) || 0)}
            disabled={disabled}
            style={{
              width: '130px',
              padding: '6px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '13px',
              textAlign: 'right',
            }}
          />
        </div>
        <span style={{ textAlign: 'right', fontSize: '13px', color: '#dc2626' }}>
          - {formatNumber(contract.discount ?? 0)}
        </span>
      </div>

      {/* 총단가 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '100px 1fr 1fr',
          padding: '12px',
          background: '#1e40af',
          borderTop: '2px solid #1e3a8a',
          fontWeight: 600,
        }}
      >
        <span style={{ fontSize: '14px', color: 'white' }}>총단가</span>
        <span style={{ textAlign: 'right', fontSize: '11px', color: '#93c5fd' }}>
          (원/월)
        </span>
        <span style={{ textAlign: 'right', fontSize: '15px', color: 'white', whiteSpace: 'nowrap' }}>
          {formatNumber(totalPrice)}
        </span>
      </div>
    </div>
  );
}

// ============================================
// 투입 인력 편집 컴포넌트 (V140.2)
// ============================================

/**
 * V75: StaffAssignmentsEditor Props
 *
 * 투입 인원 배정 목록을 관리합니다.
 * 각 인원에 등급, FTE, 역할을 지정할 수 있습니다.
 */
interface StaffingEditorProps {
  // V75: 새 스키마
  /** V75: 실투입 인원 FTE (등급별) */
  actualStaffing?: GradeBasedFTE;
  /** V75: 투입 인원 배정 목록 */
  staffAssignments?: StaffAssignment[];
  /** V75: 실투입 FTE 변경 콜백 */
  onChangeActualStaffing?: (actualStaffing: GradeBasedFTE) => void;
  /** V75: 투입 인원 배정 변경 콜백 */
  onChangeStaffAssignments?: (staffAssignments: StaffAssignment[]) => void;

  // 기존 스키마 (하위호환)
  lead: ProjectLead | undefined;
  members: StaffMember[];
  onChangeLead: (lead: ProjectLead | undefined) => void;
  onChangeMembers: (members: StaffMember[]) => void;

  disabled?: boolean;
  /** V140.3: 조직관리 스코프 조직 ID 목록 */
  scopeOrgUnitIds?: string[];
  /** V140.3: 스코프 모드 */
  scopeMode?: 'SELF' | 'SUBTREE' | 'ALL';
  /** V114.4: 프로젝트 투입인원 모드 (true: org scope 무시, company 기반 필터) */
  forProjectStaff?: boolean;
}

function StaffingEditor({
  actualStaffing,
  staffAssignments = [],
  onChangeActualStaffing,
  onChangeStaffAssignments,
  lead,
  members,
  onChangeLead,
  onChangeMembers,
  disabled = false,
  scopeOrgUnitIds,
  scopeMode,
  forProjectStaff = false,
}: StaffingEditorProps) {
  const [showLeadPicker, setShowLeadPicker] = useState(false);
  const [showMemberPicker, setShowMemberPicker] = useState(false);
  // V69 (2026-05-19): 외부 인력 생성 모달
  const [showExternalCreator, setShowExternalCreator] = useState(false);

  // V75: staffAssignments 우선, 없으면 lead/members fallback
  const useV75 = onChangeStaffAssignments !== undefined;

  // 이미 선택된 직원 ID 목록 (중복 방지)
  // V69: 외부 인력(userId 없음) 은 excludeIds 에서 제외 (StaffPickerModal 의 직원 검색에 영향 없음)
  const excludeIds = useMemo(() => {
    const ids: string[] = [];
    if (useV75) {
      staffAssignments.forEach((a) => { if (a.userId) ids.push(a.userId); });
    } else {
      if (lead) ids.push(lead.userId);
      members.forEach((m) => ids.push(m.userId));
    }
    return ids;
  }, [useV75, staffAssignments, lead, members]);

  // V81: 투입 인원 추가 (등급/FTE는 기본값 - UI에서 미사용, 등급별 집계는 actualStaffing 사용)
  const handleAddAssignment = (emp: Employee) => {
    if (onChangeStaffAssignments) {
      onChangeStaffAssignments([
        ...staffAssignments,
        {
          userId: emp.id,
          nameSnapshot: emp.nameKo,
          grade: '초급',  // V54.6: 기본값 '초급' (등급무관 제외)
          fte: 1.0,
          role: '',
        },
      ]);
    }
    setShowMemberPicker(false);
  };

  // V75: 투입 인원 제거
  const handleRemoveAssignment = (userId: string) => {
    if (onChangeStaffAssignments) {
      onChangeStaffAssignments(staffAssignments.filter((a) => a.userId !== userId));
    }
  };

  // V69 (2026-05-19): 외부 인력 추가
  // - users 컬렉션 미수정 (가짜 직원 생성 금지)
  // - V69 (2026-05-19 v2): memo 자동 저장 제거 — 기존 default '야근 없음' 가 비고에 노출되는 이슈
  //   해소 (FE/BE string mismatch RCA). memo 는 사용자가 명시 입력 시에만 저장.
  // - V69 (2026-05-20): grade 필수 입력. 등급무관 제외. actualStaffing 의 해당 등급 키 자동 +1
  //   (기존 actualStaffing.any 하드코딩 폐기).
  const handleAddExternal = (input: { externalName: string; externalOrgName: string; grade: StaffGrade }) => {
    const trimmedName = (input.externalName || '').trim();
    const trimmedOrg = (input.externalOrgName || '').trim();
    if (!trimmedName || !trimmedOrg) return;
    if (onChangeStaffAssignments) {
      onChangeStaffAssignments([
        ...staffAssignments,
        {
          isExternal: true,
          userId: '',
          externalName: trimmedName,
          externalOrgName: trimmedOrg,
          nameSnapshot: trimmedName,
          grade: input.grade,
          fte: 1.0,
          role: '',
          memo: '',
        },
      ]);
    }
    // V69 (2026-05-20): actualStaffing 의 해당 등급 키 자동 +1 (validateStaffGradeMatchActualStaffing 통과)
    if (onChangeActualStaffing) {
      const fteKey = FTE_KEYS[input.grade];  // e.g., '특급' → 'special'
      onChangeActualStaffing({
        special: actualStaffing?.special ?? 0,
        senior: actualStaffing?.senior ?? 0,
        mid: actualStaffing?.mid ?? 0,
        junior: actualStaffing?.junior ?? 0,
        any: actualStaffing?.any ?? 0,
        [fteKey]: (actualStaffing?.[fteKey] ?? 0) + 1.0,
      });
    }
    setShowExternalCreator(false);
  };

  // V69: 외부 인력 제거 (index 기반 — userId 없음)
  // V69 (2026-05-20): 제거 시에도 removed.grade 기반 actualStaffing 해당 키 -fte.
  //   기존 데이터(grade='등급무관')는 any 에서 차감 (자연 호환).
  const handleRemoveExternal = (idx: number) => {
    if (!onChangeStaffAssignments) return;
    const removed = staffAssignments[idx];
    if (!removed) return;
    onChangeStaffAssignments(staffAssignments.filter((_, i) => i !== idx));
    if (removed.isExternal && onChangeActualStaffing) {
      const fteKey = FTE_KEYS[removed.grade] ?? 'any';
      const cur = actualStaffing?.[fteKey] ?? 0;
      const newVal = Math.max(0, cur - (removed.fte || 1.0));
      onChangeActualStaffing({
        special: actualStaffing?.special ?? 0,
        senior: actualStaffing?.senior ?? 0,
        mid: actualStaffing?.mid ?? 0,
        junior: actualStaffing?.junior ?? 0,
        any: actualStaffing?.any ?? 0,
        [fteKey]: newVal,
      });
    }
  };

  // V122: 개인별 FTE 변경 핸들러
  const handleAssignmentFteChange = (userId: string, newFte: number) => {
    if (!onChangeStaffAssignments) return;
    onChangeStaffAssignments(
      staffAssignments.map((a) =>
        a.userId === userId ? { ...a, fte: Math.max(0, Math.min(1, newFte)) } : a
      )
    );
  };

  // V129: 개인별 등급 변경 핸들러
  const handleAssignmentGradeChange = (userId: string, newGrade: StaffGrade) => {
    if (!onChangeStaffAssignments) return;
    onChangeStaffAssignments(
      staffAssignments.map((a) =>
        a.userId === userId ? { ...a, grade: newGrade } : a
      )
    );
  };

  // 기존 방식: 리드 선택
  const handleSelectLead = (emp: Employee) => {
    onChangeLead({
      userId: emp.id,
      nameSnapshot: emp.nameKo,
      grade: '초급',  // V54.6: 기본값 '초급' (등급무관 제외)
    });
    setShowLeadPicker(false);
  };

  // 기존 방식: 리드 제거
  const handleRemoveLead = () => {
    onChangeLead(undefined);
  };

  // 기존 방식: 리드 등급 변경
  const handleLeadGradeChange = (grade: StaffGrade) => {
    if (lead) {
      onChangeLead({ ...lead, grade });
    }
  };

  // 기존 방식: 멤버 추가
  const handleAddMember = (emp: Employee) => {
    onChangeMembers([
      ...members,
      {
        userId: emp.id,
        nameSnapshot: emp.nameKo,
        grade: '초급',  // V54.6: 기본값 '초급' (등급무관 제외)
      },
    ]);
    setShowMemberPicker(false);
  };

  // 기존 방식: 멤버 제거
  const handleRemoveMember = (userId: string) => {
    onChangeMembers(members.filter((m) => m.userId !== userId));
  };

  // 기존 방식: 멤버 등급 변경
  const handleMemberGradeChange = (userId: string, grade: StaffGrade) => {
    onChangeMembers(
      members.map((m) => (m.userId === userId ? { ...m, grade } : m))
    );
  };

  const gradeSelectStyle = {
    padding: '4px 8px',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '12px',
    background: disabled ? '#f8fafc' : 'white',
  };

  // V75: 역할 목록 - V80에서 제거 (TL 선택만 사용)

  // V75: 총 FTE 계산
  const totalAssignmentsFte = staffAssignments.reduce((sum, a) => sum + a.fte, 0);

  // V79: 현재 TL 찾기
  const currentTL = staffAssignments.find((a) => a.role === 'TL');

  // V79: TL 변경 핸들러
  const handleTLChange = (newTLUserId: string) => {
    if (!onChangeStaffAssignments) return;

    // 새 TL이 '' (없음)인 경우: 기존 TL의 role만 해제
    if (!newTLUserId) {
      onChangeStaffAssignments(
        staffAssignments.map((a) => a.role === 'TL' ? { ...a, role: '' } : a)
      );
      return;
    }

    // 새 TL 지정: 기존 TL 해제 + 새 TL 지정
    onChangeStaffAssignments(
      staffAssignments.map((a) => {
        if (a.userId === newTLUserId) {
          return { ...a, role: 'TL' };
        }
        if (a.role === 'TL') {
          return { ...a, role: '' };
        }
        return a;
      })
    );
  };

  // V81: 투입인원 등급별 숫자 변경 핸들러
  const handleActualStaffingChange = (grade: StaffGrade, value: number) => {
    if (!onChangeActualStaffing) return;
    const current = actualStaffing || createEmptyGradeBasedFTE();
    const newActualStaffing: GradeBasedFTE = {
      special: grade === '특급' ? Math.max(0, value) : current.special,
      senior: grade === '고급' ? Math.max(0, value) : current.senior,
      mid: grade === '중급' ? Math.max(0, value) : current.mid,
      junior: grade === '초급' ? Math.max(0, value) : current.junior,
      any: grade === '등급무관' ? Math.max(0, value) : current.any,
    };
    onChangeActualStaffing(newActualStaffing);
  };

  // V81: 등급별 투입인원 맵
  const actualByGrade = useMemo(() => {
    const map: Record<StaffGrade, number> = {
      '특급': actualStaffing?.special || 0,
      '고급': actualStaffing?.senior || 0,
      '중급': actualStaffing?.mid || 0,
      '초급': actualStaffing?.junior || 0,
      '등급무관': actualStaffing?.any || 0,
    };
    return map;
  }, [actualStaffing]);

  // V81: 총 투입인원
  const totalActualStaffing = (actualStaffing?.special || 0) + (actualStaffing?.senior || 0) +
    (actualStaffing?.mid || 0) + (actualStaffing?.junior || 0) + (actualStaffing?.any || 0);

  // V75 모드: staffAssignments 사용
  if (useV75) {
    return (
      <div>
        {/* V81: 투입인원 등급별 숫자 입력 (계약인원과 동일한 형식) */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            {/* 헤더 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '100px 1fr',
                background: '#f9fafb',
                padding: '10px 12px',
                borderBottom: '1px solid #e5e7eb',
                fontSize: '12px',
                fontWeight: 600,
                color: '#6b7280',
              }}
            >
              <span>등급</span>
              <span style={{ textAlign: 'center' }}>인원</span>
            </div>

            {/* V88: 등급별 행 - 실투입에서 '등급무관' 제거 */}
            {STAFF_GRADES_NO_ANY.map((grade, idx) => {
              const headcount = actualByGrade[grade] || 0;
              return (
                <div
                  key={grade}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '100px 1fr',
                    padding: '8px 12px',
                    borderBottom: idx < STAFF_GRADES_NO_ANY.length - 1 ? '1px solid #f3f4f6' : 'none',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: '13px', color: '#374151' }}>{grade}</span>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={headcount}
                      onChange={(e) => handleActualStaffingChange(grade, parseFloat(e.target.value) || 0)}
                      disabled={disabled}
                      style={{
                        width: '60px',
                        padding: '4px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '13px',
                        textAlign: 'center',
                      }}
                    />
                  </div>
                </div>
              );
            })}

            {/* 합계 행 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '100px 1fr',
                padding: '10px 12px',
                background: '#f0fdf4',
                borderTop: '1px solid #e5e7eb',
                fontWeight: 600,
              }}
            >
              <span style={{ fontSize: '13px', color: '#047857' }}>합계</span>
              <span style={{ textAlign: 'center', fontSize: '13px', color: '#047857' }}>{formatMM(totalActualStaffing)} MM</span>
            </div>
          </div>
        </div>

        {/* V94: 프로젝트 리드 선택 */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '6px', display: 'block' }}>
            프로젝트 리드
          </label>
          <select
            value={currentTL?.userId || ''}
            onChange={(e) => handleTLChange(e.target.value)}
            disabled={disabled || staffAssignments.length === 0}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: currentTL ? '1px solid #059669' : '1px solid #fbbf24',
              borderRadius: '6px',
              fontSize: '13px',
              background: currentTL ? '#ecfdf5' : '#fffbeb',
              color: currentTL ? '#047857' : '#92400e',
              fontWeight: 500,
            }}
          >
            <option value="">{staffAssignments.length === 0 ? '실투입 인원을 먼저 추가하세요' : '-- 리드 미지정 --'}</option>
            {/* V69 (2026-05-19): 외부/기타 인력은 리드 선택 대상에서 제외 (직원 개념 아님) */}
            {staffAssignments.filter((a) => !a.isExternal).map((a) => (
              <option key={a.userId} value={a.userId}>
                {a.nameSnapshot || a.userId}
              </option>
            ))}
          </select>
          {!currentTL && staffAssignments.length > 0 && (
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#d97706' }}>
              ⚠️ 프로젝트 리드가 지정되지 않았습니다
            </p>
          )}
        </div>

        {/* V75: 실투입 인원 배정 */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
              실투입 인원 ({staffAssignments.length}명)
            </label>
            {!disabled && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => setShowMemberPicker(true)}
                  style={{
                    padding: '4px 10px',
                    border: '1px solid #059669',
                    borderRadius: '4px',
                    background: 'white',
                    color: '#059669',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Plus size={12} />
                  인원 추가
                </button>
                {/* V69 (2026-05-19): 외부/기타 인력 추가 — users 컬렉션 미사용 */}
                <button
                  onClick={() => setShowExternalCreator(true)}
                  style={{
                    padding: '4px 10px',
                    border: '1px solid #6b7280',
                    borderRadius: '4px',
                    background: 'white',
                    color: '#6b7280',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Plus size={12} />
                  실투입 인원 생성
                </button>
              </div>
            )}
          </div>

          {staffAssignments.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {staffAssignments.map((assignment, idx) => {
                // V122: 리드만 녹색, 나머지는 회색 + FTE 입력 추가
                const isLead = assignment.role === 'TL';
                // V69 (2026-05-19): 외부 인력 — 회색 톤 + [기타] 라벨
                const isExt = assignment.isExternal === true;
                const chipBg = isLead ? '#ecfdf5' : (isExt ? '#f3f4f6' : '#f8fafc');
                const chipBorder = isLead ? '#6ee7b7' : (isExt ? '#9ca3af' : '#e2e8f0');
                const chipColor = isLead ? '#047857' : (isExt ? '#4b5563' : '#374151');
                const rowKey = isExt ? `external_${idx}_${assignment.externalName || ''}` : (assignment.userId || `row_${idx}`);

                // V69: 외부 인력 row — 등급/FTE select 비활성 (기본값 표시), 삭제만 허용
                if (isExt) {
                  return (
                    <div
                      key={rowKey}
                      style={{
                        padding: '8px 12px',
                        background: chipBg,
                        borderRadius: '8px',
                        border: `1px dashed ${chipBorder}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                      }}
                    >
                      <span style={{ fontWeight: 500, fontSize: '13px', color: chipColor, flex: 1 }}>
                        <span style={{ background: '#e5e7eb', color: '#374151', fontSize: '11px', padding: '2px 6px', borderRadius: '4px', marginRight: '6px' }}>[기타]</span>
                        {assignment.externalOrgName} / {assignment.externalName}
                      </span>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {assignment.grade} · {formatMM(assignment.fte)} MM
                      </span>
                      {!disabled && (
                        <button
                          onClick={() => handleRemoveExternal(idx)}
                          style={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            color: '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          title="외부 인력 제거"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  );
                }

                return (
                <div
                  key={rowKey}
                  style={{
                    padding: '8px 12px',
                    background: chipBg,
                    borderRadius: '8px',
                    border: `1px solid ${chipBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                  }}
                >
                  <span style={{ fontWeight: 500, fontSize: '13px', color: chipColor, flex: 1 }}>
                    {isLead && '★ '}{assignment.nameSnapshot || assignment.userId}
                  </span>
                  {/* V129: 투입 등급 선택 */}
                  <select
                    value={assignment.grade}
                    onChange={(e) => handleAssignmentGradeChange(assignment.userId || '', e.target.value as StaffGrade)}
                    disabled={disabled}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '12px',
                      background: 'white',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      minWidth: '60px',
                    }}
                  >
                    {/* V54.6: 실투입 등급 선택에서 '등급무관' 완전 제외 */}
                    {STAFF_GRADES_NO_ANY.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  {/* V122: FTE 입력 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <select
                      value={assignment.fte}
                      onChange={(e) => handleAssignmentFteChange(assignment.userId || '', parseFloat(e.target.value))}
                      disabled={disabled}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: 'white',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value={0.5}>0.5</option>
                      <option value={1.0}>1.0</option>
                    </select>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>MM</span>
                  </div>
                  {/* 삭제 */}
                  {!disabled && (
                    <button
                      onClick={() => handleRemoveAssignment(assignment.userId || '')}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        padding: '0',
                        color: '#94a3b8',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                );
              })}
              {/* V122: 투입 인원 FTE 합계 */}
              <div style={{
                padding: '8px 12px',
                background: '#f0fdf4',
                borderRadius: '8px',
                border: '1px solid #86efac',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ fontWeight: 600, fontSize: '13px', color: '#047857' }}>
                  투입 MM 합계
                </span>
                <span style={{ fontWeight: 600, fontSize: '13px', color: '#047857' }}>
                  {formatMM(staffAssignments.reduce((sum, a) => sum + a.fte, 0))} MM
                </span>
              </div>
            </div>
          ) : (
            <div
              style={{
                padding: '12px',
                background: '#f8fafc',
                borderRadius: '6px',
                border: '1px dashed #cbd5e1',
                textAlign: 'center',
                color: '#94a3b8',
                fontSize: '13px',
              }}
            >
              실투입 인원이 없습니다
            </div>
          )}
        </div>

        {/* V75: 직원 선택 모달 */}
        {/* V114.4: forProjectStaff 모드 적용 (org scope 무시, company 기반 필터) */}
        <StaffPickerModal
          isOpen={showMemberPicker}
          onClose={() => setShowMemberPicker(false)}
          onSelect={handleAddAssignment}
          title="실투입 인원 추가"
          excludeIds={excludeIds}
          scopeOrgUnitIds={scopeOrgUnitIds}
          scopeMode={scopeMode}
          forProjectStaff={forProjectStaff}
        />
        {/* V69 (2026-05-19): 외부/기타 인력 생성 모달 */}
        <ExternalStaffCreateModal
          isOpen={showExternalCreator}
          onClose={() => setShowExternalCreator(false)}
          onSubmit={handleAddExternal}
        />
      </div>
    );
  }

  // 기존 방식: lead + members 모드 (레거시 호환)
  return (
    <div>
      {/* V94: 프로젝트 리드 */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
            프로젝트 리드
          </label>
          {!disabled && !lead && (
            <button
              onClick={() => setShowLeadPicker(true)}
              style={{
                padding: '4px 10px',
                border: '1px solid #4f46e5',
                borderRadius: '4px',
                background: 'white',
                color: '#4f46e5',
                cursor: 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Plus size={12} />
              리드 지정
            </button>
          )}
        </div>

        {lead ? (
          <div
            style={{
              padding: '10px 12px',
              background: '#f0f9ff',
              borderRadius: '6px',
              border: '1px solid #bae6fd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <span style={{ fontWeight: 500, color: '#0369a1' }}>{lead.nameSnapshot}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <select
                value={lead.grade}
                onChange={(e) => handleLeadGradeChange(e.target.value as StaffGrade)}
                disabled={disabled}
                style={gradeSelectStyle}
              >
                {/* V54.6: 실투입 등급 선택에서 '등급무관' 완전 제외 */}
                {STAFF_GRADES_NO_ANY.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
              {!disabled && (
                <button
                  onClick={handleRemoveLead}
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    color: '#64748b',
                  }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: '12px',
              background: '#f8fafc',
              borderRadius: '6px',
              border: '1px dashed #cbd5e1',
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: '13px',
            }}
          >
            리드가 지정되지 않았습니다
          </div>
        )}
      </div>

      {/* 실투입 인원 */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
            실투입 인원 ({members.length}명)
          </label>
          {!disabled && (
            <button
              onClick={() => setShowMemberPicker(true)}
              style={{
                padding: '4px 10px',
                border: '1px solid #059669',
                borderRadius: '4px',
                background: 'white',
                color: '#059669',
                cursor: 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Plus size={12} />
              인원 추가
            </button>
          )}
        </div>

        {members.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {members.map((member) => (
              <div
                key={member.userId}
                style={{
                  padding: '10px 12px',
                  background: 'white',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontWeight: 500, fontSize: '13px' }}>{member.nameSnapshot}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <select
                    value={member.grade}
                    onChange={(e) => handleMemberGradeChange(member.userId, e.target.value as StaffGrade)}
                    disabled={disabled}
                    style={gradeSelectStyle}
                  >
                    {/* V54.6: 실투입 등급 선택에서 '등급무관' 완전 제외 */}
                    {STAFF_GRADES_NO_ANY.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  {!disabled && (
                    <button
                      onClick={() => handleRemoveMember(member.userId)}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        padding: '2px',
                        color: '#64748b',
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: '12px',
              background: '#f8fafc',
              borderRadius: '6px',
              border: '1px dashed #cbd5e1',
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: '13px',
            }}
          >
            실투입 인원이 없습니다
          </div>
        )}
      </div>

      {/* 직원 선택 모달 (V140.3: 스코프 기반 조회) */}
      {/* V114.4: forProjectStaff 모드 적용 (org scope 무시, company 기반 필터) */}
      <StaffPickerModal
        isOpen={showLeadPicker}
        onClose={() => setShowLeadPicker(false)}
        onSelect={handleSelectLead}
        title="프로젝트 리드 선택"
        excludeIds={excludeIds}
        scopeOrgUnitIds={scopeOrgUnitIds}
        scopeMode={scopeMode}
        forProjectStaff={forProjectStaff}
      />
      <StaffPickerModal
        isOpen={showMemberPicker}
        onClose={() => setShowMemberPicker(false)}
        onSelect={handleAddMember}
        title="실투입 인원 추가"
        excludeIds={excludeIds}
        scopeOrgUnitIds={scopeOrgUnitIds}
        scopeMode={scopeMode}
        forProjectStaff={forProjectStaff}
      />
    </div>
  );
}

export default OrgProjectsPage;
